import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type Dispatch, type PointerEvent as ReactPointerEvent, type SetStateAction } from "react";
import {
  ArrowLeft,
  BarChart3,
  Bookmark,
  BookOpenText,
  BookOpen,
  CalendarDays,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CircleHelp,
  Clock3,
  Copy,
  EyeOff,
  Flame,
  PanelsTopLeft,
  Library as LibraryIcon,
  Layers,
  LayoutGrid,
  LogOut,
  LoaderCircle,
  MoreHorizontal,
  ExternalLink,
  Moon,
  PanelTopClose,
  PanelTopOpen,
  Award,
  ScanText,
  Search,
  Settings2,
  ShieldCheck,
  Sparkles,
  Volume2,
  Sun,
  Trash2,
  Undo2,
  Upload,
  X
} from "lucide-react";
import { Link, Navigate, Route, Routes, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { readerPageOcclusionShift, type SupportPanelBounds } from "./reader-occlusion";
import { GuidedPanelView } from "./guided-panel-view";
import { loadMangaPanels, oppositeSupportSide, regionIsInsidePanel, type MangaPanel } from "./manga-panels";
import {
  deleteOcrRegion,
  deleteVocabularyWord,
  excludeVolumeVocabularyWord,
  getCachedVolumeSynopsis,
  loadBook,
  loadLibrary,
  loadBookReadingTime,
  loadOcrRegions,
  loadOcrStatus,
  loadPageReadingTime,
  loadSlowPageReview,
  loadVocabulary,
  loadVolumeVocabularyAnalysis,
  loadVolumeSynopsis,
  loadReadingGoalsDashboard,
  loadReadingTimeSummary,
  loadReadingTimeMonthly,
  recognizePageRegion,
  recordReadingTime,
  restoreVolumeVocabularyWord,
  retryVolumeOcrIndex,
  retryVolumeSpeechIndex,
  loadVolumeCuratedSupport,
  saveReadingGoals,
  saveProgress,
  uploadArchive
} from "./api";
import { confirmAuthSetup, loadAuthStatus, login, logout, startAuthSetup, type AuthStatus } from "./api";
import { useMediaQuery, useShellTheme, type ShellTheme } from "./hooks";
import { toHiragana } from "./japanese";
import { groupLibraryBooks, seriesCoverBooks, sortLibraryBooks, type LibrarySeriesGroup } from "./library";
import { isPageTurnSwipe } from "./reader-gestures";
import { contourGroupForRegion } from "./region-groups";
import { applyReadingTimeToGoals, currentReadingGoalRanges, readingGoalProgress, type ReadingGoalCelebration } from "./reading-goals";
import { summarizeReadingHabits, type ReadingHabitSummary } from "./reading-habits";
import { summarizeReadingStreaks } from "./reading-streaks";
import { getSharedReadingTimeOutbox, ReadingTimeRecorder, type SendReadingTimeEvent } from "./reading-time";
import { AssistOverlay, SupportPanel, type NormalizedRegion, type SupportState } from "./reader-support";
import { filterAndSortVolumeVocabulary, formatVolumeVocabularyPartOfSpeech, paginateVolumeVocabulary, topVolumeVocabulary, volumeVocabularyIdentity, volumeVocabularyProgress, type VolumeVocabularySort } from "./volume-vocabulary";
import type { BookDetail, BookReadingTime, ComprehensionStatus, CuratedReaderSupportStatus, LibraryBook, MangaPage, OcrRegion, PageReadingTimeEntry, PageReadingTimeSummary, ReadingGoals, ReadingGoalsDashboard, ReadingTimeMonthSummary, ReadingTimeSegment, VolumeSynopsis, VocabularyWord, VolumeVocabularyAnalysis, VolumeVocabularyEntry, VolumeVocabularyExclusion } from "./types";

export function App() {
  const [shellTheme, toggleShellTheme] = useShellTheme();
  const [auth, setAuth] = useState<AuthStatus | null>(null);
  const [authError, setAuthError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    loadAuthStatus(controller.signal)
      .then(setAuth)
      .catch((error) => setAuthError(error instanceof Error ? error.message : "Could not check sign-in"));
    return () => controller.abort();
  }, []);

  if (!auth) {
    return (
      <main className={`auth-shell theme-${shellTheme}`}>
        <div className="auth-card auth-loading" role="status">
          <LoaderCircle className="spin" size={24} />
          <span>{authError ?? "Opening your library…"}</span>
        </div>
      </main>
    );
  }

  if (!auth.configured) return <SetupScreen theme={shellTheme} onToggleTheme={toggleShellTheme} onAuthenticated={setAuth} />;
  if (!auth.authenticated) return <LoginScreen theme={shellTheme} onToggleTheme={toggleShellTheme} onAuthenticated={setAuth} />;

  const handleLogout = async () => {
    await logout();
    setAuth({ configured: true, authenticated: false, username: null });
  };

  return (
    <Routes>
      <Route path="/" element={<LibraryScreen theme={shellTheme} onToggleTheme={toggleShellTheme} onLogout={handleLogout} />} />
      <Route path="/reading-time" element={<ReadingTimeMonthScreen theme={shellTheme} onToggleTheme={toggleShellTheme} onLogout={handleLogout} />} />
      <Route path="/words" element={<VocabularyScreen theme={shellTheme} onToggleTheme={toggleShellTheme} onLogout={handleLogout} />} />
      <Route path="/review" element={<ReviewScreen theme={shellTheme} onToggleTheme={toggleShellTheme} onLogout={handleLogout} />} />
      <Route path="/books/:bookId" element={<VolumeOverviewScreen theme={shellTheme} onToggleTheme={toggleShellTheme} onLogout={handleLogout} />} />
      <Route path="/books/:bookId/vocabulary" element={<VolumeVocabularyScreen theme={shellTheme} onToggleTheme={toggleShellTheme} onLogout={handleLogout} />} />
      <Route path="/read/:bookId" element={<ReaderScreen />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

type ThemeProps = { theme: ShellTheme; onToggleTheme: () => void };
type AuthScreenProps = ThemeProps & { onAuthenticated: (status: AuthStatus) => void };

function SetupScreen({ theme, onToggleTheme, onAuthenticated }: AuthScreenProps) {
  const [stage, setStage] = useState<"account" | "authenticator">("account");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [code, setCode] = useState("");
  const [provisioning, setProvisioning] = useState<{ qrCode: string; secret: string } | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [passwordCopied, setPasswordCopied] = useState(false);

  const begin = async (event: React.FormEvent) => {
    event.preventDefault();
    if (password !== confirmPassword) {
      setMessage("Those passwords do not match");
      return;
    }
    setBusy(true);
    setMessage(null);
    try {
      setProvisioning(await startAuthSetup(username, password));
      setStage("authenticator");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Could not begin setup");
    } finally {
      setBusy(false);
    }
  };

  const confirm = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setMessage(null);
    try {
      onAuthenticated(await confirmAuthSetup(code));
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Could not finish setup");
    } finally {
      setBusy(false);
    }
  };

  const copyPassword = async () => {
    try {
      await navigator.clipboard.writeText(password);
      setPasswordCopied(true);
      setMessage(null);
    } catch {
      setShowPassword(true);
      setMessage("Press and hold the password to copy it before finishing setup");
    }
  };

  return (
    <main className={`auth-shell theme-${theme}`}>
      <section className="auth-card">
        <div className="auth-theme"><ThemeToggle theme={theme} onToggle={onToggleTheme} /></div>
        <div className="auth-mark"><ShieldCheck size={25} strokeWidth={1.7} /></div>
        <p className="eyebrow">Private library</p>
        <h1>{stage === "account" ? "Secure your reader" : "Add your authenticator"}</h1>
        <p className="auth-intro">
          {stage === "account"
            ? "Create the owner account before this library can be opened from the internet."
            : "Scan this code with your authenticator app, then enter the six-digit code it gives you."}
        </p>
        {stage === "account" ? (
          <form className="auth-form" onSubmit={(event) => void begin(event)}>
            <label>Username<input autoComplete="username" value={username} onChange={(event) => setUsername(event.target.value)} minLength={2} maxLength={80} required autoFocus /></label>
            <label>Password<input type="password" autoComplete="new-password" value={password} onChange={(event) => setPassword(event.target.value)} minLength={12} maxLength={256} required /></label>
            <label>Confirm password<input type="password" autoComplete="new-password" value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} minLength={12} maxLength={256} required /></label>
            {message && <p className="auth-message" role="alert">{message}</p>}
            <button className="primary-button auth-submit" disabled={busy}>{busy && <LoaderCircle className="spin" size={18} />} Continue</button>
          </form>
        ) : (
          <form className="auth-form" onSubmit={(event) => void confirm(event)}>
            <section className="auth-password-save" aria-labelledby="save-password-heading">
              <div>
                <strong id="save-password-heading">Save your password before continuing</strong>
                <p>Safari may not store a generated password until this final step succeeds.</p>
              </div>
              <label>Username<input name="username" autoComplete="username" value={username} readOnly /></label>
              <label>Password<input name="password" type={showPassword ? "text" : "password"} autoComplete="new-password" value={password} readOnly /></label>
              <div className="auth-password-actions">
                <button className="secondary-button" type="button" onClick={() => void copyPassword()}>
                  {passwordCopied ? <Check size={17} /> : <Copy size={17} />}
                  {passwordCopied ? "Copied" : "Copy password"}
                </button>
                <button className="secondary-button" type="button" onClick={() => setShowPassword((visible) => !visible)}>
                  {showPassword ? "Hide" : "Show"}
                </button>
              </div>
            </section>
            {provisioning && <img className="auth-qr" src={provisioning.qrCode} alt="Authenticator setup QR code" />}
            {provisioning && <details className="auth-secret"><summary>Can’t scan it?</summary><code>{provisioning.secret}</code></details>}
            <label>Six-digit code<input className="otp-input" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength={6} value={code} onChange={(event) => setCode(event.target.value.replace(/\D/g, "").slice(0, 6))} required autoFocus /></label>
            {message && <p className="auth-message" role="alert">{message}</p>}
            <button className="primary-button auth-submit" disabled={busy || code.length !== 6}>{busy && <LoaderCircle className="spin" size={18} />} Finish setup</button>
          </form>
        )}
      </section>
    </main>
  );
}

function LoginScreen({ theme, onToggleTheme, onAuthenticated }: AuthScreenProps) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setMessage(null);
    try {
      onAuthenticated(await login(username, password, code));
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Could not sign in");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className={`auth-shell theme-${theme}`}>
      <section className="auth-card">
        <div className="auth-theme"><ThemeToggle theme={theme} onToggle={onToggleTheme} /></div>
        <div className="auth-mark"><BookOpen size={24} strokeWidth={1.7} /></div>
        <p className="eyebrow" lang="ja">メクリ</p>
        <h1>Welcome back</h1>
        <p className="auth-intro">Use your password and current authenticator code to open your library.</p>
        <form className="auth-form" onSubmit={(event) => void submit(event)}>
          <label>Username<input autoComplete="username" value={username} onChange={(event) => setUsername(event.target.value)} required autoFocus /></label>
          <label>Password<input type="password" autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} required /></label>
          <label>Six-digit code<input className="otp-input" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength={6} value={code} onChange={(event) => setCode(event.target.value.replace(/\D/g, "").slice(0, 6))} required /></label>
          {message && <p className="auth-message" role="alert">{message}</p>}
          <button className="primary-button auth-submit" disabled={busy || code.length !== 6}>{busy && <LoaderCircle className="spin" size={18} />} Sign in</button>
        </form>
      </section>
    </main>
  );
}

function LogoutButton({ onLogout }: { onLogout: () => Promise<void> }) {
  return <button className="secondary-button logout-button" type="button" onClick={() => void onLogout()} title="Sign out"><LogOut size={18} /><span>Sign out</span></button>;
}

function ThemeToggle({ theme, onToggle }: { theme: ShellTheme; onToggle: () => void }) {
  const night = theme === "night";
  const label = night ? "Use day mode" : "Use night mode";
  return (
    <button className="theme-button" type="button" onClick={onToggle} aria-label={label} title={label}>
      {night ? <Sun size={18} /> : <Moon size={18} />}
    </button>
  );
}

function MobileAppMenu({ theme, onToggleTheme, onLogout }: ThemeProps & { onLogout: () => Promise<void> }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const mobileMenu = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!mobileMenuOpen) return;

    const closeOnOutsidePress = (event: PointerEvent) => {
      if (!mobileMenu.current?.contains(event.target as Node)) setMobileMenuOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMobileMenuOpen(false);
    };

    document.addEventListener("pointerdown", closeOnOutsidePress);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOnOutsidePress);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [mobileMenuOpen]);

  return (
    <div className="mobile-library-menu" ref={mobileMenu}>
      <button
        className="mobile-library-menu-trigger"
        type="button"
        aria-label={mobileMenuOpen ? "Close app menu" : "Open app menu"}
        aria-expanded={mobileMenuOpen}
        aria-controls="mobile-app-menu-panel"
        title={mobileMenuOpen ? "Close menu" : "More"}
        onClick={() => setMobileMenuOpen((open) => !open)}
      >
        {mobileMenuOpen ? <X size={19} /> : <MoreHorizontal size={21} />}
      </button>
      {mobileMenuOpen && (
        <nav className="mobile-library-menu-panel" id="mobile-app-menu-panel" aria-label="App menu">
          <p>App menu</p>
          <Link to="/words" onClick={() => setMobileMenuOpen(false)}><Bookmark size={18} /><span>Saved words</span></Link>
          <Link to="/review" onClick={() => setMobileMenuOpen(false)}><CircleHelp size={18} /><span>Review reading</span></Link>
          <span className="mobile-library-menu-divider" aria-hidden="true" />
          <button type="button" onClick={() => { onToggleTheme(); setMobileMenuOpen(false); }}>
            {theme === "night" ? <Sun size={18} /> : <Moon size={18} />}
            <span>{theme === "night" ? "Use day mode" : "Use night mode"}</span>
          </button>
          <button className="mobile-menu-signout" type="button" onClick={() => { setMobileMenuOpen(false); void onLogout(); }}><LogOut size={18} /><span>Sign out</span></button>
        </nav>
      )}
    </div>
  );
}

function ShellUtilityActions({ theme, onToggleTheme, onLogout }: ThemeProps & { onLogout: () => Promise<void> }) {
  return <>
    <div className="desktop-shell-utilities"><ThemeToggle theme={theme} onToggle={onToggleTheme} /><LogoutButton onLogout={onLogout} /></div>
    <div className="mobile-shell-utilities"><MobileAppMenu theme={theme} onToggleTheme={onToggleTheme} onLogout={onLogout} /></div>
  </>;
}

function LibraryScreen({ theme, onToggleTheme, onLogout }: ThemeProps & { onLogout: () => Promise<void> }) {
  const [books, setBooks] = useState<LibraryBook[]>([]);
  const [libraryView, setLibraryView] = useState<"volumes" | "series">(() => {
    if (typeof window === "undefined") return "volumes";
    try {
      return window.localStorage.getItem("manga-reader-library-view") === "series" ? "series" : "volumes";
    } catch {
      return "volumes";
    }
  });
  const [loading, setLoading] = useState(true);
  const [monthlyReadingTimeMs, setMonthlyReadingTimeMs] = useState<number | null | undefined>(undefined);
  const [readingGoalsDashboard, setReadingGoalsDashboard] = useState<ReadingGoalsDashboard | null | undefined>(undefined);
  const [message, setMessage] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const [pollRevision, setPollRevision] = useState(0);
  const fileInput = useRef<HTMLInputElement>(null);

  const refresh = useCallback(async (signal?: AbortSignal) => {
    const payload = await loadLibrary(signal);
    setBooks(payload.books);
    setMessage(payload.import.message);
    setImporting(payload.import.state === "importing");
    setLoading(false);
    return payload.import.state;
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    let timer: number | undefined;
    const poll = async () => {
      try {
        const importState = await refresh(controller.signal);
        if (importState === "importing") {
          timer = window.setTimeout(poll, 1200);
        }
      } catch (error) {
        if (!(error instanceof DOMException && error.name === "AbortError")) {
          setMessage(error instanceof Error ? error.message : "Could not load the library");
          setLoading(false);
        }
      }
    };
    void poll();
    return () => {
      controller.abort();
      if (timer) window.clearTimeout(timer);
    };
  }, [pollRevision, refresh]);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    let refreshRequest = 0;
    let boundaryTimer: number | undefined;
    let outboxSyncInFlight = false;
    const loadReadingMetrics = async () => {
      const request = ++refreshRequest;
      const now = new Date();
      const monthFrom = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
      const monthTo = new Date(now.getFullYear(), now.getMonth() + 1, 1).toISOString();
      const goalRanges = currentReadingGoalRanges(now);
      const [monthResult, goalsResult] = await Promise.allSettled([
        loadReadingTimeSummary(monthFrom, monthTo, controller.signal),
        loadReadingGoalsDashboard(goalRanges.day, goalRanges.week, controller.signal)
      ]);
      if (!active || controller.signal.aborted || request !== refreshRequest) return;

      setMonthlyReadingTimeMs(monthResult.status === "fulfilled" ? monthResult.value.totalMs : null);
      setReadingGoalsDashboard(goalsResult.status === "fulfilled" ? goalsResult.value : null);
    };
    const refresh = () => {
      void loadReadingMetrics().catch((error) => {
        if (!active || (error instanceof DOMException && error.name === "AbortError")) return;
        setMonthlyReadingTimeMs(null);
        setReadingGoalsDashboard(null);
      });
    };
    const syncQueuedReadingTime = () => {
      if (outboxSyncInFlight) return;
      outboxSyncInFlight = true;
      void (async () => {
        const outbox = getSharedReadingTimeOutbox();
        let sent = false;
        let remaining = 0;
        let failed = false;
        do {
          const result = await outbox.drain(async (bookId, event, options) => {
            await recordReadingTime(bookId, event, options);
          });
          sent ||= result.sent > 0;
          remaining = result.remaining;
          failed = result.failed;
        } while (active && !failed && remaining > 0);
        if (active && sent) refresh();
      })()
        .finally(() => {
          outboxSyncInFlight = false;
        });
    };
    const scheduleBoundaryRefresh = () => {
      if (boundaryTimer !== undefined) window.clearTimeout(boundaryTimer);
      const now = new Date();
      const nextDay = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
      boundaryTimer = window.setTimeout(() => {
        refresh();
        syncQueuedReadingTime();
        scheduleBoundaryRefresh();
      }, Math.max(1_000, nextDay.getTime() - now.getTime() + 100));
    };
    const resume = () => {
      if (document.visibilityState !== "visible") return;
      refresh();
      syncQueuedReadingTime();
      scheduleBoundaryRefresh();
    };

    refresh();
    syncQueuedReadingTime();
    scheduleBoundaryRefresh();
    document.addEventListener("visibilitychange", resume);
    window.addEventListener("pageshow", resume);
    return () => {
      active = false;
      controller.abort();
      document.removeEventListener("visibilitychange", resume);
      window.removeEventListener("pageshow", resume);
      if (boundaryTimer !== undefined) window.clearTimeout(boundaryTimer);
    };
  }, []);

  const handleSaveReadingGoals = useCallback(async (input: Pick<ReadingGoals, "dailyMinutes" | "weeklyMinutes">) => {
    const goals = await saveReadingGoals(input);
    setReadingGoalsDashboard((current) => current ? { ...current, goals } : current);
  }, []);

  const handleUpload = async (file: File | undefined) => {
    if (!file) return;
    setImporting(true);
    setMessage(`Importing ${file.name}`);
    try {
      await uploadArchive(file);
      await refresh();
      setPollRevision((current) => current + 1);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Import failed");
    } finally {
      setImporting(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  };

  const continueBook = books.find((book) => book.progressUpdatedAt) ?? books[0];
  const sortedBooks = useMemo(() => sortLibraryBooks(books), [books]);
  const seriesGroups = useMemo(() => groupLibraryBooks(books), [books]);

  const changeLibraryView = (view: "volumes" | "series") => {
    setLibraryView(view);
    try {
      window.localStorage.setItem("manga-reader-library-view", view);
    } catch {
      // The selected view still lasts for the current session.
    }
  };

  return (
    <main className={`library-shell library-home theme-${theme}`}>
      <header className="library-header">
        <div className="brand-lockup">
          <span className="brand-mark" aria-hidden="true"><BookOpen size={20} strokeWidth={1.8} /></span>
          <div>
            <div className="brand-title-row">
              <h1 className="brand-title" lang="ja">メクリ</h1>
              <span className="brand-title-seal" lang="ja" aria-hidden="true">読</span>
            </div>
            <p>Your manga library</p>
          </div>
        </div>
        <div className="header-actions desktop-library-actions">
          <ThemeToggle theme={theme} onToggle={onToggleTheme} />
          <Link className="secondary-button" to="/words" aria-label="Saved words" title="Saved words"><Bookmark size={18} /><span>Saved words</span></Link>
          <Link className="secondary-button" to="/review" aria-label="Review reading" title="Review reading"><CircleHelp size={18} /><span>Review</span></Link>
          <LogoutButton onLogout={onLogout} />
          <button className="primary-button" type="button" onClick={() => fileInput.current?.click()} disabled={importing} aria-label={importing ? "Importing manga" : "Add manga"} title={importing ? "Importing manga" : "Add manga"}>
            {importing ? <LoaderCircle className="spin" size={18} /> : <Upload size={18} />}
            <span>{importing ? "Importing" : "Add manga"}</span>
          </button>
        </div>
        <div className="mobile-library-actions">
          <button className="primary-button mobile-add-button" type="button" onClick={() => fileInput.current?.click()} disabled={importing} aria-label={importing ? "Importing manga" : "Add manga"} title={importing ? "Importing manga" : "Add manga"}>
            {importing ? <LoaderCircle className="spin" size={18} /> : <Upload size={18} />}
            <span>{importing ? "Importing" : "Add"}</span>
          </button>
          <MobileAppMenu theme={theme} onToggleTheme={onToggleTheme} onLogout={onLogout} />
        </div>
        <input
          ref={fileInput}
          className="visually-hidden"
          type="file"
          accept=".cbz,.zip,.epub,application/zip,application/vnd.comicbook+zip,application/epub+zip"
          aria-label="Choose a CBZ, ZIP, or EPUB file"
          onChange={(event) => void handleUpload(event.target.files?.[0])}
        />
      </header>

      <div className="library-content">
        {message && (
          <div className={`status-line ${importing ? "is-active" : "is-error"}`} role="status">
            {importing && <LoaderCircle className="spin" size={17} />}
            <span>{message}</span>
          </div>
        )}

        {loading ? (
          <LibraryLoading />
        ) : books.length === 0 && !importing ? (
          <section className="empty-library">
            <LibraryIcon size={32} strokeWidth={1.4} />
            <h2>Your library is empty</h2>
            <p>Add a CBZ, ZIP, or DRM-free EPUB from Files to begin reading.</p>
            <button className="primary-button" type="button" onClick={() => fileInput.current?.click()}>
              <Upload size={18} /> Add manga
            </button>
          </section>
        ) : (
          <>
            {continueBook && (
              <section className="continue-section" aria-labelledby="continue-heading">
                <div className="section-heading-row">
                  <div>
                    <p className="eyebrow">Continue</p>
                    <h2 id="continue-heading">Pick up where you left off</h2>
                  </div>
                </div>
                <Link className="continue-row" to={`/read/${continueBook.id}`}>
                  <Cover book={continueBook} eager />
                  <div className="continue-copy">
                    <p className="series-name" lang="ja">{continueBook.seriesTitle}</p>
                    <h3 lang="ja">{continueBook.title}</h3>
                    <p><span lang="ja">{chapterForPage(continueBook)}</span> · Page {continueBook.currentPage + 1} of {continueBook.pageCount}</p>
                    <ProgressBar value={bookProgress(continueBook)} />
                  </div>
                  <span className="continue-action">Read <ChevronRight size={18} /></span>
                </Link>
              </section>
            )}

            <ReadingGoalsStrip
              monthlyReadingTimeMs={monthlyReadingTimeMs}
              dashboard={readingGoalsDashboard}
              onSave={handleSaveReadingGoals}
            />

            <section className="all-books" aria-labelledby="library-heading">
              <div className="section-heading-row">
                <div>
                  <p className="eyebrow">Library</p>
                  <h2 id="library-heading">{libraryView === "volumes" ? "All volumes" : "Manga series"}</h2>
                </div>
                <div className="library-heading-actions">
                  <p>{books.length} {books.length === 1 ? "volume" : "volumes"}</p>
                  <div className="library-view-switch" role="group" aria-label="Library view">
                    <button
                      className={libraryView === "volumes" ? "is-active" : undefined}
                      type="button"
                      aria-pressed={libraryView === "volumes"}
                      onClick={() => changeLibraryView("volumes")}
                    >
                      <LayoutGrid size={16} /> Volumes
                    </button>
                    <button
                      className={libraryView === "series" ? "is-active" : undefined}
                      type="button"
                      aria-pressed={libraryView === "series"}
                      onClick={() => changeLibraryView("series")}
                    >
                      <Layers size={16} /> Series
                    </button>
                  </div>
                </div>
              </div>
              {libraryView === "volumes" ? (
                <BookGrid books={sortedBooks} showSeries />
              ) : (
                <SeriesCatalogue groups={seriesGroups} />
              )}
            </section>
          </>
        )}
      </div>
    </main>
  );
}

type ReadingGoalKind = "daily" | "weekly";

function ReadingGoalsStrip({
  monthlyReadingTimeMs,
  dashboard,
  onSave
}: {
  monthlyReadingTimeMs: number | null | undefined;
  dashboard: ReadingGoalsDashboard | null | undefined;
  onSave: (input: Pick<ReadingGoals, "dailyMinutes" | "weeklyMinutes">) => Promise<void>;
}) {
  const navigate = useNavigate();
  const [editing, setEditing] = useState<ReadingGoalKind | null>(null);
  const [dailyDraft, setDailyDraft] = useState("");
  const [weeklyDraft, setWeeklyDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dailyButton = useRef<HTMLButtonElement>(null);
  const weeklyButton = useRef<HTMLButtonElement>(null);
  const dailyInput = useRef<HTMLInputElement>(null);
  const weeklyInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing === "daily") dailyInput.current?.focus();
    if (editing === "weekly") weeklyInput.current?.focus();
  }, [editing]);

  const openEditor = (kind: ReadingGoalKind) => {
    if (!dashboard) return;
    setDailyDraft(dashboard.goals.dailyMinutes?.toString() ?? "");
    setWeeklyDraft(dashboard.goals.weeklyMinutes?.toString() ?? "");
    setError(null);
    setEditing(kind);
  };

  const closeEditor = () => {
    const returnTo = editing === "weekly" ? weeklyButton : dailyButton;
    setEditing(null);
    setError(null);
    window.requestAnimationFrame(() => returnTo.current?.focus());
  };

  const parseGoal = (value: string, maximum: number, label: string): number | null => {
    if (value.trim() === "") return null;
    const parsed = Number(value);
    if (!Number.isInteger(parsed) || parsed < 1 || parsed > maximum) {
      throw new Error(`${label} must be a whole number of minutes between 1 and ${maximum.toLocaleString()}.`);
    }
    return parsed;
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setSaving(true);
    try {
      await onSave({
        dailyMinutes: parseGoal(dailyDraft, 24 * 60, "Daily goal"),
        weeklyMinutes: parseGoal(weeklyDraft, 7 * 24 * 60, "Weekly goal")
      });
      closeEditor();
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : "Could not save your reading goals");
    } finally {
      setSaving(false);
    }
  };

  const goalButton = (kind: ReadingGoalKind) => {
    const isDaily = kind === "daily";
    const label = isDaily ? "Today" : "This week";
    const unsetLabel = isDaily ? "Set daily goal" : "Set weekly goal";
    const targetMinutes = dashboard
      ? (isDaily ? dashboard.goals.dailyMinutes : dashboard.goals.weeklyMinutes)
      : null;
    const totalMs = dashboard
      ? (isDaily ? dashboard.progress.day.totalMs : dashboard.progress.week.totalMs)
      : 0;
    const progress = readingGoalProgress(totalMs, targetMinutes);
    const value = dashboard === undefined
      ? "Loading…"
      : dashboard === null
        ? "Unavailable"
        : targetMinutes === null
          ? unsetLabel
          : `${formatReadingTime(totalMs)} / ${formatReadingTime(targetMinutes * 60_000)}`;
    const accessibleValue = targetMinutes === null
      ? unsetLabel
      : `Edit ${kind} reading goal, ${formatReadingTime(totalMs)} read of ${formatReadingTime(targetMinutes * 60_000)}`;

    return (
      <button
        className={`reading-goal-segment ${progress?.complete ? "is-complete" : ""}`}
        type="button"
        ref={isDaily ? dailyButton : weeklyButton}
        onClick={() => openEditor(kind)}
        disabled={!dashboard}
        aria-label={dashboard ? accessibleValue : `${label} reading goal unavailable`}
        aria-expanded={editing === kind}
        aria-controls="reading-goals-editor"
      >
        <span className="reading-goal-label">{label}{progress?.complete && <Check size={14} strokeWidth={2.6} aria-hidden="true" />}</span>
        <strong className="reading-goal-value">{value}</strong>
        {progress && (
          <span className="reading-goal-progress" aria-hidden="true">
            <span style={{ width: `${progress.fill * 100}%` }} />
          </span>
        )}
      </button>
    );
  };

  const monthValue = monthlyReadingTimeMs === undefined
    ? "Loading…"
    : monthlyReadingTimeMs === null
      ? "Unavailable"
      : `${formatReadingTime(monthlyReadingTimeMs)} this month`;
  const monthTarget = `/reading-time?month=${monthIdentifierForDate(new Date())}`;
  const openMonthlyReadingTime = () => {
    void navigate(monthTarget);
  };

  return (
    <section className="reading-goals-strip" aria-labelledby="reading-goals-heading">
      <Link
        className="reading-time-summary-link"
        to={monthTarget}
        aria-label="Open monthly reading-time breakdown"
        onClick={openMonthlyReadingTime}
      >
        <span className="reading-time-icon" aria-hidden="true"><Clock3 size={19} /></span>
        <div>
          <h2 id="reading-goals-heading">Reading time</h2>
          <strong>{monthValue}</strong>
        </div>
        <ChevronRight size={16} aria-hidden="true" />
      </Link>
      <div className="reading-goal-switch" role="group" aria-label="Reading goals">
        {goalButton("daily")}
        {goalButton("weekly")}
      </div>
      {editing && dashboard && (
        <form
          className="reading-goal-editor"
          id="reading-goals-editor"
          onSubmit={(event) => void submit(event)}
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              event.preventDefault();
              closeEditor();
            }
          }}
        >
          <div className="reading-goal-editor-heading">
            <span>
              <strong>Set reading goals</strong>
              <small>Leave either goal blank to keep it off.</small>
            </span>
            <button className="reading-goal-close" type="button" onClick={closeEditor} aria-label="Close reading goal settings"><X size={17} /></button>
          </div>
          <div className="reading-goal-fields">
            <div className={`reading-goal-field ${editing === "daily" ? "is-selected" : ""}`}>
              <div className="reading-goal-field-heading">
                <label htmlFor="daily-reading-goal">Daily goal</label>
                {dashboard.goals.dailyMinutes !== null && <button type="button" onClick={() => setDailyDraft("")} aria-label="Remove daily goal">Remove</button>}
              </div>
              <span className="reading-goal-input">
                <input id="daily-reading-goal" ref={dailyInput} type="number" inputMode="numeric" min="1" max={24 * 60} step="1" value={dailyDraft} onChange={(event) => setDailyDraft(event.target.value)} aria-describedby="reading-goal-unit-daily" />
                <small id="reading-goal-unit-daily">minutes</small>
              </span>
            </div>
            <div className={`reading-goal-field ${editing === "weekly" ? "is-selected" : ""}`}>
              <div className="reading-goal-field-heading">
                <label htmlFor="weekly-reading-goal">Weekly goal</label>
                {dashboard.goals.weeklyMinutes !== null && <button type="button" onClick={() => setWeeklyDraft("")} aria-label="Remove weekly goal">Remove</button>}
              </div>
              <span className="reading-goal-input">
                <input id="weekly-reading-goal" ref={weeklyInput} type="number" inputMode="numeric" min="1" max={7 * 24 * 60} step="1" value={weeklyDraft} onChange={(event) => setWeeklyDraft(event.target.value)} aria-describedby="reading-goal-unit-weekly" />
                <small id="reading-goal-unit-weekly">minutes</small>
              </span>
            </div>
          </div>
          {error && <p className="reading-goal-error" role="alert">{error}</p>}
          <div className="reading-goal-editor-actions">
            <button className="secondary-button" type="button" onClick={closeEditor}>Cancel</button>
            <button className="primary-button" type="submit" disabled={saving}>{saving ? <LoaderCircle className="spin" size={17} /> : <Settings2 size={17} />} Save goals</button>
          </div>
        </form>
      )}
    </section>
  );
}

function ReadingTimeMonthScreen({ theme, onToggleTheme, onLogout }: ThemeProps & { onLogout: () => Promise<void> }) {
  const [searchParams] = useSearchParams();
  const month = parseMonthIdentifier(searchParams.get("month"), new Date());
  const monthRange = monthRangeFromIdentifier(month);
  const habitContext = readingHabitContextForMonth(month);
  const [summary, setSummary] = useState<ReadingTimeMonthSummary | null | undefined>(undefined);
  const [habitHistory, setHabitHistory] = useState<ReadingTimeMonthSummary | null | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [selectedDayId, setSelectedDayId] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setError(null);
    setSummary(undefined);
    loadReadingTimeMonthly(monthRange.from, monthRange.to, controller.signal)
      .then((next) => {
        if (!controller.signal.aborted) setSummary(next);
      })
      .catch((reason) => {
        if (reason instanceof DOMException && reason.name === "AbortError") return;
        setSummary(null);
        setError(reason instanceof Error ? reason.message : "Could not load monthly reading time");
      });
    return () => controller.abort();
  }, [monthRange.from, monthRange.to]);

  useEffect(() => {
    const controller = new AbortController();
    setHabitHistory(undefined);
    loadReadingTimeMonthly(habitContext.from, habitContext.to, controller.signal)
      .then((next) => {
        if (!controller.signal.aborted) setHabitHistory(next);
      })
      .catch((reason) => {
        if (reason instanceof DOMException && reason.name === "AbortError") return;
        setHabitHistory(null);
      });
    return () => controller.abort();
  }, [habitContext.from, habitContext.to]);

  useEffect(() => setSelectedDayId(null), [month.year, month.month]);

  const rows = useMemo(() => {
    const days = month.daysInMonth;
    const dailyTotals = new Map<string, ReadingTimeMonthSummary["byDay"][number]>();
    for (const entry of summary?.byDay ?? []) {
      dailyTotals.set(entry.day, entry);
    }
    const computed = [];
    let maxDailyMs = 0;
    for (let dayIndex = 1; dayIndex <= days; dayIndex++) {
      const date = new Date(month.year, month.month, dayIndex);
      const key = monthDayId(month.year, month.month, dayIndex);
      const reading = dailyTotals.get(key);
      const totalMs = reading?.totalMs ?? 0;
      computed.push({
        day: dayIndex,
        dayLabel: String(dayIndex),
        weekdayLabel: date.toLocaleDateString(undefined, { weekday: "short" }),
        totalMs,
        byBook: reading?.byBook ?? []
      });
      if (totalMs > maxDailyMs) maxDailyMs = totalMs;
    }
    return computed.map((entry) => ({ ...entry, relativeWidth: maxDailyMs === 0 ? 0 : Math.min(100, (entry.totalMs / maxDailyMs) * 100) }));
  }, [month.year, month.month, month.daysInMonth, summary?.byDay, summary?.totalMs]);

  const previousMonthDate = new Date(month.year, month.month - 1, 1);
  const nextMonthDate = new Date(month.year, month.month + 1, 1);
  const previousMonthTarget = `/reading-time?month=${monthIdentifierForDate(previousMonthDate)}`;
  const nextMonthTarget = `/reading-time?month=${monthIdentifierForDate(nextMonthDate)}`;
  const previousMonthLabel = previousMonthDate.toLocaleDateString(undefined, { month: "long", year: "numeric" });
  const nextMonthLabel = nextMonthDate.toLocaleDateString(undefined, { month: "long", year: "numeric" });
  const leadingCalendarDays = (new Date(month.year, month.month, 1).getDay() + 6) % 7;
  const hasRecordedReading = rows.some((row) => row.totalMs > 0);
  const hasSummary = summary !== undefined && summary !== null && error === null;
  const monthTotal = summary === undefined ? "Loading…" : summary === null || error ? "Unavailable" : formatReadingTime(summary.totalMs);
  const summaryError = error ?? (summary === null ? "Could not load this month" : null);
  const now = new Date();
  const todayId = monthDayId(now.getFullYear(), now.getMonth(), now.getDate());
  const monthlyInsights = useMemo(() => {
    const activeDays = rows.filter((row) => row.totalMs > 0);
    const bestDay = activeDays.reduce<(typeof rows)[number] | null>((best, row) => !best || row.totalMs > best.totalMs ? row : best, null);
    const bookTotals = new Map<string, { bookId: string; title: string; seriesTitle: string; totalMs: number }>();

    for (const row of activeDays) {
      for (const book of row.byBook) {
        const previous = bookTotals.get(book.bookId);
        bookTotals.set(book.bookId, { ...book, totalMs: (previous?.totalMs ?? 0) + book.totalMs });
      }
    }

    const mostRead = [...bookTotals.values()]
      .sort((left, right) => right.totalMs - left.totalMs || left.title.localeCompare(right.title))[0] ?? null;
    const streaks = summarizeReadingStreaks(rows.map((row) => ({
      day: monthDayId(month.year, month.month, row.day),
      totalMs: row.totalMs
    })), todayId);

    return {
      activeDays: activeDays.length,
      averageMs: activeDays.length === 0 ? 0 : Math.round(activeDays.reduce((total, row) => total + row.totalMs, 0) / activeDays.length),
      bestDay,
      mostRead,
      streaks
    };
  }, [month.month, month.year, rows, todayId]);
  const habitInsights = useMemo<ReadingHabitSummary | null | undefined>(() => (
    habitHistory ? summarizeReadingHabits(habitHistory.byDay, habitContext.asOfDay) : habitHistory
  ), [habitContext.asOfDay, habitHistory]);
  const calendarStreaks = habitInsights?.streaks ?? monthlyInsights.streaks;
  const streakValue = habitInsights === undefined
    ? "Loading…"
    : habitInsights === null
      ? "Unavailable"
      : habitInsights.streaks.currentDays > 0
        ? `${habitInsights.streaks.currentDays} ${habitInsights.streaks.currentDays === 1 ? "day" : "days"}`
        : habitContext.historical
          ? "No streak then"
          : "No live streak";
  const streakBest = habitInsights && habitInsights.streaks.longestDays > 0
    ? `Best: ${habitInsights.streaks.longestDays} ${habitInsights.streaks.longestDays === 1 ? "day" : "days"} in the past year`
    : habitInsights
      ? "One reading day starts it"
      : null;
  return (
    <main className={`library-shell reading-time-shell theme-${theme}`}>
      <header className="library-header reading-time-header">
        <Link className="reading-time-mobile-back" to="/" aria-label="Back to library"><ArrowLeft size={20} /></Link>
        <div className="brand-lockup">
          <span className="brand-mark" aria-hidden="true"><BookOpen size={20} strokeWidth={1.8} /></span>
          <div>
            <h1>{month.label}</h1>
            <p>Daily reading-time breakdown</p>
          </div>
        </div>
        <div className="header-actions">
          <ShellUtilityActions theme={theme} onToggleTheme={onToggleTheme} onLogout={onLogout} />
        </div>
      </header>

      <div className="library-content">
        <section className="section-heading-row reading-time-heading-row" aria-labelledby="monthly-reading-time-heading">
          <div>
            <p className="eyebrow">Reading time</p>
            <h2 id="monthly-reading-time-heading">{month.label}</h2>
          </div>
          <Link className="secondary-button" to="/"><ArrowLeft size={18} /> Back to library</Link>
        </section>

        <section className="reading-time-month-view">
          <section className="reading-time-month-overview" aria-label={`${month.label} at a glance`}>
            <div className="reading-time-month-total">
              <span><Clock3 size={16} aria-hidden="true" /> Total reading</span>
              <strong className={summaryError ? "is-unavailable" : ""}>{monthTotal}</strong>
              <small>{monthlyInsights.activeDays > 0 ? `Across ${monthlyInsights.activeDays} active ${monthlyInsights.activeDays === 1 ? "day" : "days"}` : "Ready for your first session"}</small>
            </div>
            <dl className="reading-time-month-insights">
              <div>
                <dt><BookOpenText size={15} aria-hidden="true" /> Active days</dt>
                <dd>{hasSummary ? monthlyInsights.activeDays : "—"}</dd>
              </div>
              <div>
                <dt><BarChart3 size={15} aria-hidden="true" /> Daily average</dt>
                <dd>{hasSummary ? formatReadingTime(monthlyInsights.averageMs) : "—"}</dd>
              </div>
              <div>
                <dt><Flame size={15} aria-hidden="true" /> {habitContext.historical ? "Streak then" : "Current streak"}</dt>
                <dd>
                  <strong>{streakValue}</strong>
                  {streakBest && <small>{streakBest}</small>}
                </dd>
              </div>
              <div>
                <dt><Award size={15} aria-hidden="true" /> Best day</dt>
                <dd>
                  {monthlyInsights.bestDay ? <><strong>{monthlyInsights.bestDay.weekdayLabel} {monthlyInsights.bestDay.dayLabel}</strong><small>{formatReadingTime(monthlyInsights.bestDay.totalMs)}</small></> : "—"}
                </dd>
              </div>
              <div className="reading-time-month-most-read">
                <dt><Bookmark size={15} aria-hidden="true" /> Most read</dt>
                <dd>
                  {monthlyInsights.mostRead ? <><strong lang="ja">{monthlyInsights.mostRead.seriesTitle}</strong><small>{monthlyInsights.mostRead.title} · {formatReadingTime(monthlyInsights.mostRead.totalMs)}</small></> : "—"}
                </dd>
              </div>
            </dl>
          </section>

          <ReadingHabitsPanel summary={habitInsights} asOfLabel={habitContext.label} />

          <nav className="reading-time-month-navigation" aria-label="Choose a reading-time month">
            <Link to={previousMonthTarget} aria-label={`Show reading time for ${previousMonthLabel}`}>
              <ArrowLeft size={17} aria-hidden="true" />
              <span><small>Previous</small><strong>{previousMonthLabel}</strong></span>
            </Link>
            <Link to={nextMonthTarget} aria-label={`Show reading time for ${nextMonthLabel}`}>
              <span><small>Next</small><strong>{nextMonthLabel}</strong></span>
              <ChevronRight size={17} aria-hidden="true" />
            </Link>
          </nav>

          {summaryError && !hasSummary ? (
            <p className="reading-time-month-error" role="alert">{summaryError}</p>
          ) : summary === undefined ? (
            <p className="status-line" role="status">Loading daily reading totals…</p>
          ) : (
            <>
              {!hasRecordedReading && <p className="reading-time-month-empty">No reading time recorded this month yet.</p>}
              <div className="reading-time-calendar-heading">
                <strong>Daily activity</strong>
                <small>Tap a date to see its volumes</small>
              </div>
              <div className="reading-time-month-calendar-scroll">
                <div className="reading-time-month-weekdays" aria-hidden="true">
                  {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((weekday) => <span key={weekday}>{weekday}</span>)}
                </div>
                <ol className="reading-time-month-calendar" aria-label={`Daily reading time for ${month.label}`}>
                  {Array.from({ length: leadingCalendarDays }, (_, index) => (
                    <li key={`empty-${index}`} className="reading-time-month-day is-empty" aria-hidden="true" />
                  ))}
                  {rows.map((row) => {
                    const dateTime = monthDayId(month.year, month.month, row.day);
                    const isToday = dateTime === todayId;
                    const isSelected = dateTime === selectedDayId;
                    const isStreakDay = calendarStreaks.streakDays.has(dateTime);
                    const isCurrentStreakDay = calendarStreaks.currentStreakDays.has(dateTime);
                    const dayDetailId = `reading-time-day-detail-${dateTime}`;
                    const dayDetailHeadingId = `${dayDetailId}-heading`;
                    const calendarColumn = (leadingCalendarDays + row.day - 1) % 7;
                    const popoutAlignment = calendarColumn <= 1 ? "align-start" : calendarColumn >= 5 ? "align-end" : "align-center";
                    const dayDetailLabel = new Date(month.year, month.month, row.day).toLocaleDateString(undefined, {
                      weekday: "long",
                      day: "numeric",
                      month: "long",
                      year: "numeric"
                    });
                    return (
                      <li
                        key={row.day}
                        className={`reading-time-month-day ${row.totalMs > 0 ? "has-reading" : ""} ${isToday ? "is-today" : ""} ${isSelected ? "is-selected" : ""}`}
                      >
                        <button
                          type="button"
                          onClick={() => setSelectedDayId((current) => current === dateTime ? null : dateTime)}
                          aria-expanded={isSelected}
                          aria-controls={dayDetailId}
                          aria-label={`${row.weekdayLabel} ${row.dayLabel}: ${row.totalMs > 0 ? `${formatReadingTime(row.totalMs)} reading time` : "no reading time"}. ${isSelected ? "Hide" : "Show"} day detail`}
                        >
                          <span className="reading-time-month-day-heading">
                            <time dateTime={dateTime} aria-label={`${row.weekdayLabel} ${row.dayLabel}`} aria-current={isToday ? "date" : undefined}>
                              {row.dayLabel}
                            </time>
                            <span className="reading-time-month-day-markers" aria-hidden="true">
                              {isStreakDay && <Flame className={`reading-time-month-day-flame ${isCurrentStreakDay ? "is-current" : "is-broken"}`} size={13} />}
                              {isToday && <span>Today</span>}
                            </span>
                          </span>
                          {row.totalMs > 0 && <strong aria-hidden="true">{formatReadingTime(row.totalMs)}</strong>}
                          {row.totalMs > 0 && <span className="reading-time-month-day-bar" aria-hidden="true"><span style={{ width: `${row.relativeWidth}%` }} /></span>}
                        </button>
                        {isSelected && (
                          <section className={`reading-time-day-detail ${popoutAlignment}`} id={dayDetailId} aria-labelledby={dayDetailHeadingId}>
                            <header>
                              <div>
                                <p>Day detail</p>
                                <h3 id={dayDetailHeadingId}>{dayDetailLabel}</h3>
                              </div>
                              <button type="button" onClick={() => setSelectedDayId(null)} aria-label="Close day detail"><X size={17} /></button>
                            </header>
                            <div className="reading-time-day-detail-body">
                              <div className="reading-time-day-detail-total">
                                <Clock3 size={18} aria-hidden="true" />
                                <span>
                                  <strong>{row.totalMs > 0 ? formatReadingTime(row.totalMs) : "No reading"}</strong>
                                  <small>{row.byBook.length > 0 ? `${row.byBook.length} ${row.byBook.length === 1 ? "volume" : "volumes"}` : "Nothing recorded on this day"}</small>
                                </span>
                              </div>
                              {row.byBook.length > 0 && (
                                <ul aria-label={`Volumes read on ${dayDetailLabel}`}>
                                  {row.byBook.map((book) => (
                                    <li key={book.bookId}>
                                      <Link to={`/books/${book.bookId}`}>
                                        <span>
                                          <strong className="reading-time-month-day-series" lang="ja">{book.seriesTitle}</strong>
                                          <small className="reading-time-month-day-volume">{book.title}</small>
                                        </span>
                                        <span className="reading-time-day-detail-duration">{formatReadingTime(book.totalMs)} <ChevronRight size={15} aria-hidden="true" /></span>
                                      </Link>
                                      <span className="reading-time-day-detail-bar" aria-hidden="true"><span style={{ width: `${row.totalMs > 0 ? (book.totalMs / row.totalMs) * 100 : 0}%` }} /></span>
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          </section>
                        )}
                      </li>
                    );
                  })}
                </ol>
              </div>
            </>
          )}
        </section>
      </div>
    </main>
  );
}

function ReadingHabitsPanel({
  summary,
  asOfLabel
}: {
  summary: ReadingHabitSummary | null | undefined;
  asOfLabel: string;
}) {
  if (summary === undefined) {
    return (
      <section className="reading-habits" aria-labelledby="reading-habits-heading">
        <header>
          <div><p className="eyebrow">Reading rhythm</p><h3 id="reading-habits-heading">The shape of your reading</h3></div>
          <p>Looking across your recent reading days…</p>
        </header>
      </section>
    );
  }
  if (summary === null) {
    return (
      <section className="reading-habits" aria-labelledby="reading-habits-heading">
        <header>
          <div><p className="eyebrow">Reading rhythm</p><h3 id="reading-habits-heading">The shape of your reading</h3></div>
          <p>Reading rhythm is unavailable right now.</p>
        </header>
      </section>
    );
  }

  const currentStreak = summary.streaks.currentDays > 0
    ? `${summary.streaks.currentDays} ${summary.streaks.currentDays === 1 ? "day" : "days"}`
    : "No live streak";
  const bestStreak = summary.streaks.longestDays > 0
    ? `Best: ${summary.streaks.longestDays} ${summary.streaks.longestDays === 1 ? "day" : "days"} in the past year`
    : "One reading day starts it";
  const recentPace = summary.previousFourWeeksMs === 0
    ? summary.recentFourWeeksMs > 0
      ? "A fresh rhythm in the last four weeks"
      : "No sessions in the last eight weeks"
    : summary.recentChangePercent !== null && Math.abs(summary.recentChangePercent) < 5
      ? "About even with the prior four weeks"
      : `${Math.abs(summary.recentChangePercent ?? 0)}% ${summary.recentChangePercent && summary.recentChangePercent > 0 ? "more" : "less"} than the prior four weeks`;
  const lastVisibleHeatmapDay = summary.heatmapDays.findLast((day) => !day.future);
  const heatmapLabel = summary.heatmapDays.length > 0 && lastVisibleHeatmapDay
    ? `Reading activity from ${readingHabitDayLabel(summary.heatmapDays[0]!.day)} to ${readingHabitDayLabel(lastVisibleHeatmapDay.day)}`
    : "Reading activity for the last twelve weeks";

  return (
    <section className="reading-habits" aria-labelledby="reading-habits-heading">
      <header>
        <div>
          <p className="eyebrow">Reading rhythm</p>
          <h3 id="reading-habits-heading">The shape of your reading</h3>
        </div>
        <p>A gentle look at when manga is fitting into your life. {asOfLabel}.</p>
      </header>
      <div className="reading-habits-body">
        <div className="reading-habit-map-panel">
          <div className="reading-habit-map-heading">
            <span><strong>Last 12 weeks</strong><small>{summary.activeWeeks} active {summary.activeWeeks === 1 ? "week" : "weeks"}</small></span>
            <span className="reading-habit-map-legend" aria-hidden="true">Less <i className="level-1" /><i className="level-2" /><i className="level-3" /><i className="level-4" /> More</span>
          </div>
          <div className="reading-habit-map-layout">
            <div className="reading-habit-weekdays" aria-hidden="true"><span>Mon</span><span>Tue</span><span>Wed</span><span>Thu</span><span>Fri</span><span>Sat</span><span>Sun</span></div>
            <div className="reading-habit-heatmap" role="img" aria-label={heatmapLabel}>
              {summary.heatmapDays.map((day) => (
                <span
                  className={`level-${day.intensity} ${day.future ? "is-future" : ""}`}
                  key={day.day}
                  title={day.future ? undefined : `${readingHabitDayLabel(day.day)} · ${day.totalMs > 0 ? formatReadingTime(day.totalMs) : "No reading"}`}
                />
              ))}
            </div>
          </div>
        </div>
        <dl className="reading-habit-readouts">
          <div>
            <dt><Flame size={15} aria-hidden="true" /> Current streak</dt>
            <dd><strong>{currentStreak}</strong><small>{bestStreak}</small></dd>
          </div>
          <div>
            <dt><Clock3 size={15} aria-hidden="true" /> Typical reading day</dt>
            <dd><strong>{summary.typicalDayMs > 0 ? formatReadingTime(summary.typicalDayMs) : "No pattern yet"}</strong><small>Median active day, last 12 weeks</small></dd>
          </div>
          <div>
            <dt><CalendarDays size={15} aria-hidden="true" /> Most natural day</dt>
            <dd>
              <strong>{summary.favoriteWeekday?.label ?? "No pattern yet"}</strong>
              <small>{summary.favoriteWeekday ? `${summary.favoriteWeekday.activeDays} active ${summary.favoriteWeekday.label}s in 12 weeks` : "Your usual day will emerge naturally"}</small>
            </dd>
          </div>
          <div>
            <dt><BarChart3 size={15} aria-hidden="true" /> Recent pace</dt>
            <dd><strong>{summary.recentFourWeeksMs > 0 ? formatReadingTime(summary.recentFourWeeksMs) : "No recent time"}</strong><small>{recentPace}</small></dd>
          </div>
        </dl>
      </div>
    </section>
  );
}

function SeriesCatalogue({ groups }: { groups: LibrarySeriesGroup[] }) {
  const [expandedSeriesId, setExpandedSeriesId] = useState<string | null>(null);
  const expandedSeries = groups.find((group) => group.id === expandedSeriesId) ?? null;

  return (
    <div className="series-catalogue">
      <div className="series-grid">
        {groups.map((group) => {
          const expanded = group.id === expandedSeriesId;
          const covers = seriesCoverBooks(group.books);
          return (
            <button
              className={`series-tile ${expanded ? "is-expanded" : ""}`}
              type="button"
              onClick={() => setExpandedSeriesId((current) => current === group.id ? null : group.id)}
              aria-expanded={expanded}
              aria-controls={`series-volumes-${group.id}`}
              key={group.id}
            >
              <span className={`series-cover-mosaic has-${covers.length}`} aria-hidden="true">
                {covers.map((book) => book.coverUrl ? (
                  <img src={book.coverUrl} alt="" loading="lazy" key={book.id} />
                ) : (
                  <span className="series-cover-placeholder" key={book.id}>{book.title.slice(0, 1)}</span>
                ))}
              </span>
              <span className="series-tile-copy">
                <span>
                  <strong lang="ja">{group.title}</strong>
                  <small>{group.books.length} {group.books.length === 1 ? "volume" : "volumes"}</small>
                </span>
                <ChevronDown size={18} aria-hidden="true" />
              </span>
            </button>
          );
        })}
      </div>

      {expandedSeries && (
        <section className="series-volume-drawer" id={`series-volumes-${expandedSeries.id}`} aria-labelledby={`expanded-series-${expandedSeries.id}`}>
          <div className="series-volume-heading">
            <div>
              <p className="eyebrow">Volumes</p>
              <h3 id={`expanded-series-${expandedSeries.id}`} lang="ja">{expandedSeries.title}</h3>
            </div>
            <button type="button" onClick={() => setExpandedSeriesId(null)}>Close</button>
          </div>
          <BookGrid books={expandedSeries.books} />
        </section>
      )}
    </div>
  );
}

function BookGrid({
  books,
  showSeries = false
}: {
  books: LibraryBook[];
  showSeries?: boolean;
}) {
  return (
    <div className="book-grid">
      {books.map((book) => (
        <article className="book-tile" key={book.id}>
          <Link className="book-tile-cover-link" to={`/books/${book.id}`} aria-label={`Open overview for ${book.title}`}>
            <Cover book={book} />
          </Link>
          <div className="book-tile-copy">
            <Link className="book-tile-title" to={`/books/${book.id}`}>
              <h3 lang="ja">{showSeries ? book.seriesTitle : book.title}</h3>
            </Link>
            {showSeries && <p lang="ja">{book.title}</p>}
            {!showSeries && <p>{book.progressUpdatedAt ? `${Math.round(bookProgress(book) * 100)}% read` : "Not started"}</p>}
            <ProgressBar value={bookProgress(book)} />
          </div>
        </article>
      ))}
    </div>
  );
}

function SpeechPreparationStatus({
  book,
  onRetry,
  showEmpty = false
}: {
  book: Pick<LibraryBook, "id" | "speechIndex">;
  onRetry: (bookId: string) => Promise<void>;
  showEmpty?: boolean;
}) {
  const [retrying, setRetrying] = useState(false);
  const { status, processedRegions, totalRegions, discovering } = book.speechIndex;
  if (status === "complete" && totalRegions === 0 && !showEmpty) return null;
  const progress = totalRegions > 0 ? Math.min(1, processedRegions / totalRegions) : 0;
  const label = discovering
    ? "Finding dialogue for voice"
    : status === "complete" && totalRegions === 0
      ? "No dialogue to prepare yet"
      : status === "complete"
        ? "Voice ready"
        : status === "error"
      ? "Voice preparation paused"
      : totalRegions > 0
        ? `Preparing voice ${Math.round(progress * 100)}%`
        : "Voice preparation queued";
  if (status === "error") {
    return (
      <button
        className="book-speech-status is-error"
        type="button"
        disabled={retrying}
        onClick={() => {
          setRetrying(true);
          void onRetry(book.id)
            .catch(() => undefined)
            .finally(() => setRetrying(false));
        }}
        title="Resume voice preparation"
      >
        {retrying ? <LoaderCircle className="spin" size={13} /> : <Volume2 size={13} />}
        <span>{retrying ? "Resuming voice…" : "Resume voice preparation"}</span>
      </button>
    );
  }
  return (
    <span className={`book-speech-status is-${status}`} title={label} aria-label={label}>
      {status === "queued" || status === "processing" ? <LoaderCircle className="spin" size={13} /> : <Volume2 size={13} />}
      <span>{label}</span>
      {(status === "queued" || status === "processing") && !discovering && totalRegions > 0 && (
        <span className="book-speech-progress" aria-hidden="true"><span style={{ width: `${progress * 100}%` }} /></span>
      )}
    </span>
  );
}

function VolumeFrequencyChart({ words, compact = false }: { words: VolumeVocabularyEntry[]; compact?: boolean }) {
  const chartWords = topVolumeVocabulary(words, 15);
  const chartMaximum = chartWords[0]?.count ?? 1;
  return (
    <ol className={`frequency-chart ${compact ? "is-compact" : ""}`}>
      {chartWords.map((word) => (
        <li key={`${word.lemma}:${word.reading ?? ""}`}>
          <span className="frequency-word" lang="ja">
            <strong>{word.expression}</strong>
            {word.reading && <small>{toHiragana(word.reading)}</small>}
          </span>
          <span className="frequency-track" aria-hidden="true">
            <span style={{ width: `${Math.max(4, (word.count / chartMaximum) * 100)}%` }} />
          </span>
          <strong className="frequency-count" aria-label={`${word.count} appearances`}>{word.count}</strong>
        </li>
      ))}
    </ol>
  );
}

function VolumeOverviewScreen({ theme, onToggleTheme, onLogout }: ThemeProps & { onLogout: () => Promise<void> }) {
  const { bookId } = useParams();
  const [book, setBook] = useState<LibraryBook | null>(null);
  const [analysis, setAnalysis] = useState<VolumeVocabularyAnalysis | null>(null);
  const [analysisError, setAnalysisError] = useState<string | null>(null);
  const [analysisRevision, setAnalysisRevision] = useState(0);
  const [synopsis, setSynopsis] = useState<VolumeSynopsis | null>(() => (
    bookId ? getCachedVolumeSynopsis(bookId) : null
  ));
  const [readingTime, setReadingTime] = useState<BookReadingTime | null | undefined>(undefined);
  const [message, setMessage] = useState<string | null>(null);
  const [voiceError, setVoiceError] = useState<string | null>(null);
  const [curatedSupport, setCuratedSupport] = useState<CuratedReaderSupportStatus | null>(null);
  const [curatedSupportError, setCuratedSupportError] = useState<string | null>(null);
  const [libraryRevision, setLibraryRevision] = useState(0);

  useEffect(() => {
    if (!bookId) return;
    const controller = new AbortController();
    let timer: number | undefined;
    const poll = async () => {
      try {
        const library = await loadLibrary(controller.signal);
        const loaded = library.books.find((candidate) => candidate.id === bookId);
        if (!loaded) throw new Error("This volume is no longer in the library");
        setBook(loaded);
        setMessage(null);
        document.title = `${loaded.title} · メクリ`;
        if (loaded.speechIndex.discovering || loaded.speechIndex.status === "queued" || loaded.speechIndex.status === "processing") {
          timer = window.setTimeout(poll, 5000);
        }
      } catch (error) {
        if (!(error instanceof DOMException && error.name === "AbortError")) {
          setMessage(error instanceof Error ? error.message : "Could not open this volume");
        }
      }
    };
    void poll();
    return () => {
      controller.abort();
      if (timer) window.clearTimeout(timer);
    };
  }, [bookId, libraryRevision]);

  useEffect(() => {
    if (!bookId) return;
    const controller = new AbortController();
    let timer: number | undefined;
    const poll = async () => {
      try {
        const next = await loadVolumeVocabularyAnalysis(bookId, controller.signal);
        setAnalysis(next);
        setAnalysisError(null);
        if (next.status === "pending" || next.status === "indexing") timer = window.setTimeout(poll, 5000);
      } catch (error) {
        if (!(error instanceof DOMException && error.name === "AbortError")) {
          setAnalysisError(error instanceof Error ? error.message : "Could not open the volume analysis");
        }
      }
    };
    void poll();
    return () => {
      controller.abort();
      if (timer) window.clearTimeout(timer);
    };
  }, [analysisRevision, bookId]);

  useEffect(() => {
    if (!bookId) return;
    const controller = new AbortController();
    loadVolumeCuratedSupport(bookId, controller.signal)
      .then((support) => {
        setCuratedSupport(support);
        setCuratedSupportError(null);
      })
      .catch((error) => {
        if (!(error instanceof DOMException && error.name === "AbortError")) {
          setCuratedSupportError(error instanceof Error ? error.message : "Could not check English support");
        }
      });
    return () => controller.abort();
  }, [bookId]);

  useEffect(() => {
    if (!bookId) return;
    const controller = new AbortController();
    setSynopsis(getCachedVolumeSynopsis(bookId));
    loadBookReadingTime(bookId, controller.signal)
      .then(setReadingTime)
      .catch((error) => {
        if (!(error instanceof DOMException && error.name === "AbortError")) setReadingTime(null);
      });
    loadVolumeSynopsis(bookId, controller.signal)
      .then(setSynopsis)
      .catch((error) => {
        if (!(error instanceof DOMException && error.name === "AbortError")) {
          setSynopsis({
            status: "error",
            reason: null,
            html: null,
            sourceName: null,
            sourceUrl: null,
            fetchedAt: null,
            expiresAt: null
          });
        }
      });
    return () => controller.abort();
  }, [bookId]);

  const retrySpeech = async (targetBookId: string) => {
    setVoiceError(null);
    try {
      await retryVolumeSpeechIndex(targetBookId);
      setLibraryRevision((current) => current + 1);
    } catch (error) {
      setVoiceError(error instanceof Error ? error.message : "Could not resume voice preparation");
    }
  };

  const chartWords = topVolumeVocabulary(analysis?.chart ?? [], 15);
  const analysisProgress = analysis ? volumeVocabularyProgress(analysis.processedPages, analysis.totalPages) : 0;

  return (
    <main className={`library-shell volume-overview-shell theme-${theme}`}>
      <header className="library-header">
        <Link className="back-link" to="/"><ArrowLeft size={20} /><span>Library</span></Link>
        <div className="vocabulary-header-title">
          <h1>Volume overview</h1>
          <p>{book?.seriesTitle ?? "Opening volume…"}</p>
        </div>
        <div className="header-spacer"><ShellUtilityActions theme={theme} onToggleTheme={onToggleTheme} onLogout={onLogout} /></div>
      </header>

      <div className="library-content volume-overview-content">
        {!book && !message ? (
          <LibraryLoading />
        ) : !book ? (
          <section className="volume-overview-error" role="alert">
            <BookOpen size={30} />
            <h2>Couldn’t open this volume</h2>
            <p>{message}</p>
            <Link className="secondary-button" to="/"><ArrowLeft size={17} /> Back to library</Link>
          </section>
        ) : (
          <>
            <section className="volume-overview-hero" aria-labelledby="volume-overview-title">
              <div className="volume-overview-cover"><Cover book={book} eager /></div>
              <div className="volume-overview-copy">
                <p className="eyebrow" lang="ja">{book.seriesTitle}</p>
                <h2 id="volume-overview-title" lang="ja">{book.title}</h2>
                <p className="volume-overview-meta">{book.pageCount.toLocaleString()} pages · {book.chapterCount} {book.chapterCount === 1 ? "chapter" : "chapters"}</p>
                <div className="volume-overview-progress">
                  <span>{book.progressUpdatedAt ? `${Math.round(bookProgress(book) * 100)}% read` : "Not started"}</span>
                  <ProgressBar value={bookProgress(book)} />
                </div>
                <div className="volume-overview-actions">
                  <Link className="primary-button" to={`/read/${book.id}`}><BookOpen size={18} /> {book.progressUpdatedAt ? "Continue reading" : "Start reading"}</Link>
                  <Link className="secondary-button" to={`/books/${book.id}/vocabulary`}><BarChart3 size={17} /> Full vocabulary</Link>
                </div>
              </div>
            </section>

            <section className="volume-overview-facts" aria-label="Volume activity">
              <div>
                <Award size={19} aria-hidden="true" />
                <span><small>First read</small><strong>{readingTime === undefined ? "…" : readingTime === null ? "—" : !readingTime.completed || readingTime.firstReadMs === null ? "Not completed" : formatReadingTime(readingTime.firstReadMs)}</strong></span>
              </div>
              <div>
                <Clock3 size={19} aria-hidden="true" />
                <span><small>Total time</small><strong>{readingTime === undefined ? "…" : readingTime === null ? "—" : formatReadingTime(readingTime.totalMs)}</strong></span>
              </div>
              <div>
                <BookOpenText size={19} aria-hidden="true" />
                <span><small>Current place</small><strong>Page {Math.min(book.currentPage + 1, book.pageCount)} of {book.pageCount}</strong></span>
              </div>
              <div className="volume-overview-voice">
                <small>Voice support</small>
                <SpeechPreparationStatus book={book} onRetry={retrySpeech} showEmpty />
                {voiceError && <span className="volume-overview-voice-error" role="alert">{voiceError}</span>}
              </div>
              <div className="volume-overview-translation">
                <small>English support</small>
                <span className={`book-translation-status is-${curatedSupport?.corpusId ? "complete" : "idle"}`}>
                  <Sparkles size={13} />
                  <strong>{!curatedSupport
                    ? "Checking…"
                    : curatedSupport.corpusId
                      ? `Codex curated · ${curatedSupport.translatedRegions.toLocaleString()} translations`
                      : "Not curated yet"}</strong>
                </span>
                {curatedSupport?.corpusId && (
                  <span className="volume-overview-support-detail">
                    {`${curatedSupport.explainedRegions.toLocaleString()} explanations · ${curatedSupport.totalRegions.toLocaleString()} lines`}
                  </span>
                )}
                {curatedSupportError && <span className="volume-overview-voice-error" role="alert">{curatedSupportError}</span>}
              </div>
            </section>

            <div className="volume-overview-grid">
              <section className="volume-overview-synopsis" aria-labelledby="volume-synopsis-heading">
                <div className="volume-overview-section-heading">
                  <div><p className="eyebrow">Story</p><h3 id="volume-synopsis-heading">Volume synopsis</h3></div>
                </div>
                {!synopsis ? (
                  <p className="volume-overview-placeholder"><LoaderCircle className="spin" size={17} /> Looking for this volume’s synopsis…</p>
                ) : synopsis.status === "ready" && synopsis.html ? (
                  <>
                    <div className="volume-synopsis-copy" dangerouslySetInnerHTML={{ __html: synopsis.html }} />
                    {synopsis.sourceUrl && (
                      <a className="volume-synopsis-source" href={synopsis.sourceUrl} target="_blank" rel="noreferrer">
                        {synopsis.sourceName === "Google Books"
                          ? <img src="/poweredby-google.svg" alt="Powered by Google" width="62" height="30" />
                          : <span>{`Synopsis from ${synopsis.sourceName ?? "source"}`}</span>}
                        <ExternalLink size={13} />
                      </a>
                    )}
                  </>
                ) : (
                  <p className="volume-overview-empty">
                    {synopsis.reason === "provider-unavailable"
                      ? "The synopsis source is temporarily unavailable. This volume will try again later."
                      : "No reliable online synopsis was found for this edition."}
                  </p>
                )}
              </section>

              <section className="volume-overview-vocabulary" aria-labelledby="volume-overview-vocabulary-heading">
                <div className="volume-overview-section-heading">
                  <div><p className="eyebrow">Vocabulary</p><h3 id="volume-overview-vocabulary-heading">Common words</h3></div>
                  {analysis && <span>{analysis.uniqueWords.toLocaleString()} useful words</span>}
                </div>
                {analysisError && (
                  <div className="volume-overview-load-error" role="alert">
                    <span>{analysisError}</span>
                    <button type="button" onClick={() => {
                      setAnalysisError(null);
                      setAnalysisRevision((current) => current + 1);
                    }}>Try again</button>
                  </div>
                )}
                {!analysis && !analysisError ? (
                  <p className="volume-overview-placeholder"><LoaderCircle className="spin" size={17} /> Opening the volume analysis…</p>
                ) : analysis && (analysis.status === "pending" || analysis.status === "indexing") && chartWords.length === 0 ? (
                  <div className="volume-overview-indexing">
                    <p>Building the word profile · {analysis.totalPages > 0 ? `${analysis.processedPages} of ${analysis.totalPages} pages` : "queued"}</p>
                    <span aria-hidden="true"><span style={{ width: `${analysisProgress * 100}%` }} /></span>
                  </div>
                ) : analysis && chartWords.length === 0 ? (
                  <p className="volume-overview-empty">There are no reliable vocabulary results for this volume yet.</p>
                ) : analysis ? (
                  <VolumeFrequencyChart words={chartWords} compact />
                ) : null}
                <Link className="volume-overview-more" to={`/books/${book.id}/vocabulary`}>
                  Explore the full vocabulary <ChevronRight size={16} />
                </Link>
              </section>
            </div>
          </>
        )}
      </div>
    </main>
  );
}

type VocabularyExclusionFeedback = {
  kind: "excluded" | "restored";
  exclusion: VolumeVocabularyExclusion;
};

function useVolumeVocabularyExclusions({
  bookId,
  setAnalysis,
  onRefresh
}: {
  bookId: string | undefined;
  setAnalysis: Dispatch<SetStateAction<VolumeVocabularyAnalysis | null>>;
  onRefresh: () => void;
}) {
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<VocabularyExclusionFeedback | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!feedback) return;
    const timer = window.setTimeout(() => setFeedback(null), 7000);
    return () => window.clearTimeout(timer);
  }, [feedback]);

  const excludeWord = useCallback(async (word: VolumeVocabularyEntry) => {
    if (!bookId || busyKey) return;
    const key = volumeVocabularyIdentity(word);
    setBusyKey(key);
    setError(null);
    try {
      const exclusion = await excludeVolumeVocabularyWord(bookId, word);
      setAnalysis((current) => {
        if (!current) return current;
        const currentExclusions = current.excludedWords ?? [];
        const remainingWords = current.words.filter((entry) => volumeVocabularyIdentity(entry) !== key);
        return {
          ...current,
          words: remainingWords,
          chart: remainingWords.slice(0, current.chart.length),
          uniqueWords: Math.max(0, current.uniqueWords - 1),
          totalTokens: Math.max(0, current.totalTokens - word.count),
          excludedWords: [
            ...currentExclusions.filter((entry) => volumeVocabularyIdentity(entry) !== key),
            exclusion
          ]
        };
      });
      setFeedback({ kind: "excluded", exclusion });
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Could not exclude this word");
    } finally {
      setBusyKey(null);
    }
  }, [bookId, busyKey, setAnalysis]);

  const restoreWord = useCallback(async (exclusion: VolumeVocabularyExclusion) => {
    if (!bookId || busyKey) return;
    const key = volumeVocabularyIdentity(exclusion);
    setBusyKey(key);
    setError(null);
    try {
      await restoreVolumeVocabularyWord(bookId, exclusion);
      setAnalysis((current) => current ? {
        ...current,
        excludedWords: (current.excludedWords ?? []).filter((entry) => volumeVocabularyIdentity(entry) !== key)
      } : current);
      setFeedback({ kind: "restored", exclusion });
      onRefresh();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Could not restore this word");
    } finally {
      setBusyKey(null);
    }
  }, [bookId, busyKey, onRefresh, setAnalysis]);

  return {
    busyKey,
    feedback,
    error,
    excludeWord,
    restoreWord
  };
}

function VocabularyExclusionControl({
  word,
  controls,
  compact = false
}: {
  word: VolumeVocabularyEntry;
  controls: ReturnType<typeof useVolumeVocabularyExclusions>;
  compact?: boolean;
}) {
  const key = volumeVocabularyIdentity(word);
  const busy = controls.busyKey === key;
  return (
    <div className={`volume-word-exclusion ${compact ? "is-compact" : ""}`}>
      <button
        className="volume-word-exclusion-trigger"
        type="button"
        disabled={busy}
        title={`Hide ${word.expression} from useful vocabulary for this manga series`}
        aria-label={`Hide ${word.expression} from useful vocabulary for this manga series`}
        onClick={() => void controls.excludeWord(word)}
      >
        <EyeOff size={14} aria-hidden="true" /> {busy ? "Hiding…" : "Not useful"}
      </button>
    </div>
  );
}

function VocabularyExclusionNotice({ controls }: { controls: ReturnType<typeof useVolumeVocabularyExclusions> }) {
  if (!controls.feedback && !controls.error) return null;
  return (
    <div className={`volume-exclusion-notice ${controls.error ? "is-error" : ""}`} role={controls.error ? "alert" : "status"}>
      <span>{controls.error ?? (controls.feedback?.kind === "excluded"
        ? `${controls.feedback.exclusion.expression} is hidden throughout this manga.`
        : `${controls.feedback?.exclusion.expression} is useful again.`)}</span>
      {controls.feedback?.kind === "excluded" && (
        <button type="button" onClick={() => void controls.restoreWord(controls.feedback!.exclusion)}>
          <Undo2 size={15} /> Undo
        </button>
      )}
    </div>
  );
}

function VolumeVocabularyPager({
  page,
  totalPages,
  start,
  end,
  total,
  onPage
}: {
  page: number;
  totalPages: number;
  start: number;
  end: number;
  total: number;
  onPage: (page: number) => void;
}) {
  if (total === 0) return null;
  return (
    <nav className="volume-word-pager" aria-label="Vocabulary pages">
      <p aria-live="polite"><strong>{start}–{end}</strong> of {total}</p>
      <div>
        <button type="button" disabled={page <= 1} onClick={() => onPage(page - 1)} aria-label="Previous vocabulary page">
          <ChevronLeft size={17} /> Previous
        </button>
        <span aria-current="page">Page {page} of {totalPages}</span>
        <button type="button" disabled={page >= totalPages} onClick={() => onPage(page + 1)} aria-label="Next vocabulary page">
          Next <ChevronRight size={17} />
        </button>
      </div>
    </nav>
  );
}

function VolumeVocabularyScreen({ theme, onToggleTheme, onLogout }: ThemeProps & { onLogout: () => Promise<void> }) {
  const { bookId } = useParams();
  const [book, setBook] = useState<BookDetail | null>(null);
  const [analysis, setAnalysis] = useState<VolumeVocabularyAnalysis | null>(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<VolumeVocabularySort>("frequency");
  const [page, setPage] = useState(1);
  const [reload, setReload] = useState(0);
  const [retrying, setRetrying] = useState(false);
  const wordListHeadingRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!bookId) return;
    const controller = new AbortController();
    loadBook(bookId, controller.signal)
      .then((loaded) => {
        setBook(loaded);
        document.title = `Vocabulary · ${loaded.title}`;
      })
      .catch((reason) => {
        if (!(reason instanceof DOMException && reason.name === "AbortError")) {
          setMessage(reason instanceof Error ? reason.message : "Could not open this volume");
        }
      });
    return () => controller.abort();
  }, [bookId]);

  useEffect(() => {
    if (!bookId) return;
    const controller = new AbortController();
    let timer: number | undefined;

    const poll = async () => {
      try {
        const next = await loadVolumeVocabularyAnalysis(bookId, controller.signal);
        setAnalysis(next);
        setMessage(null);
        setLoading(false);
        if (next.status === "pending" || next.status === "indexing") {
          timer = window.setTimeout(poll, 5000);
        }
      } catch (reason) {
        if (!(reason instanceof DOMException && reason.name === "AbortError")) {
          setMessage(reason instanceof Error ? reason.message : "Could not load this volume's vocabulary");
          setLoading(false);
          timer = window.setTimeout(poll, 10_000);
        }
      }
    };

    setLoading(true);
    void poll();
    return () => {
      controller.abort();
      if (timer) window.clearTimeout(timer);
    };
  }, [bookId, reload]);

  const visibleWords = useMemo(
    () => filterAndSortVolumeVocabulary(analysis?.words ?? [], query, sort),
    [analysis?.words, query, sort]
  );
  const vocabularyPage = useMemo(() => paginateVolumeVocabulary(visibleWords, page), [page, visibleWords]);
  const excludedWords = analysis?.excludedWords ?? [];
  const chartWords = useMemo(() => topVolumeVocabulary(analysis?.chart ?? [], 15), [analysis?.chart]);
  const indexing = analysis?.status === "pending" || analysis?.status === "indexing";
  const resultsAvailable = analysis?.status === "ready" || (analysis?.status === "error" && analysis.words.length > 0);
  const progress = analysis ? volumeVocabularyProgress(analysis.processedPages, analysis.totalPages) : 0;
  const refreshAnalysis = useCallback(() => setReload((value) => value + 1), []);
  const exclusionControls = useVolumeVocabularyExclusions({ bookId, setAnalysis, onRefresh: refreshAnalysis });

  useEffect(() => setPage(1), [bookId, query, sort]);
  useEffect(() => {
    setPage((current) => paginateVolumeVocabulary(visibleWords, current).page);
  }, [visibleWords.length]);

  const changeVocabularyPage = useCallback((nextPage: number) => {
    setPage(paginateVolumeVocabulary(visibleWords, nextPage).page);
    window.requestAnimationFrame(() => {
      const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      wordListHeadingRef.current?.scrollIntoView({ behavior: reducedMotion ? "auto" : "smooth", block: "start" });
    });
  }, [visibleWords]);

  const retryIndexing = async () => {
    if (!bookId || retrying) return;
    setRetrying(true);
    try {
      await retryVolumeOcrIndex(bookId);
      setMessage(null);
      setReload((value) => value + 1);
    } catch (reason) {
      setMessage(reason instanceof Error ? reason.message : "Could not resume indexing");
    } finally {
      setRetrying(false);
    }
  };

  return (
    <main className={`library-shell volume-vocabulary-shell theme-${theme}`}>
      <header className="library-header">
        <Link className="back-link" to={bookId ? `/books/${bookId}` : "/"}><ArrowLeft size={20} /><span>Overview</span></Link>
        <div className="vocabulary-header-title">
          <h1>Volume vocabulary</h1>
          <p>{book?.title ?? "Opening volume…"}</p>
        </div>
        <div className="header-spacer"><ShellUtilityActions theme={theme} onToggleTheme={onToggleTheme} onLogout={onLogout} /></div>
      </header>

      <div className="library-content volume-vocabulary-content">
        <section className="volume-vocabulary-intro" aria-labelledby="volume-vocabulary-title">
          <div>
            <p className="eyebrow">Reading profile</p>
            <h2 id="volume-vocabulary-title" lang={book ? "ja" : undefined}>{book?.title ?? "This volume"}</h2>
            <p>Useful words found throughout this volume, with particles and everyday filler left out.</p>
          </div>
          {book && <Link className="secondary-button" to={`/read/${book.id}`}><BookOpen size={17} /> Read volume</Link>}
        </section>

        {loading && !analysis ? (
          <div className="volume-analysis-loading" role="status">
            <LoaderCircle className="spin" size={24} /> Opening the volume analysis…
          </div>
        ) : message && !analysis ? (
          <section className="volume-analysis-error" role="alert">
            <h3>Couldn’t open the analysis</h3>
            <p>{message}</p>
            <button className="secondary-button" type="button" onClick={() => setReload((value) => value + 1)}>Try again</button>
          </section>
        ) : analysis ? (
          <>
            {indexing && (
              <section className="volume-indexing" aria-live="polite">
                <div className="volume-indexing-copy">
                  <span className="volume-indexing-icon"><ScanText size={23} /></span>
                  <div>
                    <p className="eyebrow">Building your vocabulary</p>
                    <h3>{analysis.status === "pending" ? "This volume is next in line" : "Reading every page"}</h3>
                    <p>{analysis.totalPages > 0 ? `${analysis.processedPages} of ${analysis.totalPages} pages complete.` : "Preparing the volume’s pages."} You can leave this screen; indexing continues in the background.</p>
                  </div>
                  <strong>{analysis.totalPages > 0 ? `${Math.round(progress * 100)}%` : "Queued"}</strong>
                </div>
                <span className="volume-indexing-progress" aria-hidden="true">{progress > 0 && <span style={{ width: `${progress * 100}%` }} />}</span>
              </section>
            )}

            {message && analysis.status !== "error" && (
              <div className="volume-refresh-warning" role="status">
                <span>Progress could not refresh just now. Retrying automatically.</span>
                <button type="button" onClick={() => setReload((value) => value + 1)}>Retry now</button>
              </div>
            )}

            {analysis.status === "error" && (
              <section className="volume-analysis-error" role="alert">
                <h3>Indexing paused</h3>
                <p>{analysis.error || "This volume could not be analysed yet."}</p>
                {message && <p>{message}</p>}
                <button className="secondary-button" type="button" disabled={retrying} onClick={() => void retryIndexing()}>
                  {retrying ? <LoaderCircle className="spin" size={17} /> : <ScanText size={17} />}
                  {retrying ? "Resuming…" : "Resume indexing"}
                </button>
              </section>
            )}

            {resultsAvailable && (
              <>
                <section className="volume-analysis-stats" aria-label="Volume vocabulary summary">
                  <div><strong>{analysis.uniqueWords.toLocaleString()}</strong><span>useful words</span></div>
                  <div><strong>{analysis.totalTokens.toLocaleString()}</strong><span>word appearances</span></div>
                  <div><strong>{analysis.processedPages.toLocaleString()}</strong><span>pages analysed</span></div>
                </section>

                <VocabularyExclusionNotice controls={exclusionControls} />

                {analysis.status === "ready" && analysis.words.length === 0 ? (
                  <section className="empty-vocabulary volume-empty-vocabulary">
                    <BarChart3 size={30} strokeWidth={1.5} />
                    <h2>No useful words found</h2>
                    <p>The volume was indexed, but there was not enough reliable vocabulary to build an analysis.</p>
                  </section>
                ) : (
                  <>
                    <section className="frequency-section" aria-labelledby="frequency-heading">
                      <div className="volume-section-heading">
                        <div>
                          <p className="eyebrow">Frequency</p>
                          <h3 id="frequency-heading">Words that shape this volume</h3>
                        </div>
                        <p>{analysis.status === "error" ? `Top ${chartWords.length} from completed pages` : `Top ${chartWords.length} useful words`}</p>
                      </div>
                      <VolumeFrequencyChart words={chartWords} />
                    </section>

                    <section className="volume-word-section" aria-labelledby="volume-word-list-heading">
                      <div className="volume-section-heading volume-word-list-heading" ref={wordListHeadingRef}>
                        <div>
                          <p className="eyebrow">Vocabulary list</p>
                          <h3 id="volume-word-list-heading">{analysis.status === "error" ? "Useful words from completed pages" : "Every useful word"}</h3>
                        </div>
                        <p>{visibleWords.length} of {analysis.words.length}</p>
                      </div>
                      <div className="volume-word-tools">
                        <label className="volume-word-search">
                          <Search size={18} aria-hidden="true" />
                          <span className="visually-hidden">Search this volume’s vocabulary</span>
                          <input value={query} onChange={(event) => { setQuery(event.target.value); setPage(1); }} placeholder="Search Japanese, reading, or meaning" />
                        </label>
                        <label className="volume-word-sort">
                          <span>Sort</span>
                          <select value={sort} onChange={(event) => { setSort(event.target.value as VolumeVocabularySort); setPage(1); }}>
                            <option value="frequency">Most frequent</option>
                            <option value="pages">Most widespread</option>
                            <option value="alphabetical">Japanese A–Z</option>
                          </select>
                        </label>
                      </div>

                      {visibleWords.length === 0 ? (
                        <p className="volume-word-no-results">No words match “{query.trim()}”.</p>
                      ) : (
                        <div className="volume-word-list" role="table" aria-label="Volume vocabulary">
                          <div className="volume-word-list-header" role="row">
                            <span role="columnheader">Word</span><span role="columnheader">Meaning</span><span role="columnheader">Frequency</span>
                          </div>
                          {vocabularyPage.items.map((word) => {
                            const tags = formatVolumeVocabularyPartOfSpeech(word);
                            const firstPage = word.firstPage;
                            return (
                              <article className="volume-word-row" role="row" key={`${word.lemma}:${word.reading ?? ""}`}>
                                <div className="volume-word-expression" role="cell" lang="ja">
                                  <strong>{word.expression}</strong>
                                  {word.reading && <span>{toHiragana(word.reading)}</span>}
                                  {word.lemma !== word.expression && <small>Base: {word.lemma}</small>}
                                </div>
                                <div className="volume-word-meaning" role="cell">
                                  <p>{word.definitions.slice(0, 2).join("; ") || "No dictionary meaning"}</p>
                                  {tags.length > 0 && <span className="volume-word-tags">{tags.map((tag) => <small key={tag}>{tag}</small>)}</span>}
                                </div>
                                <div className="volume-word-usage" role="cell">
                                  <strong>{word.count}×</strong>
                                  <span>across {word.pageCount} {word.pageCount === 1 ? "page" : "pages"}</span>
                                  {bookId && <Link to={`/read/${bookId}?page=${firstPage}${word.firstRegionId ? `&region=${word.firstRegionId}` : ""}`}>First on page {firstPage + 1} <ChevronRight size={14} /></Link>}
                                  <VocabularyExclusionControl word={word} controls={exclusionControls} />
                                </div>
                              </article>
                            );
                          })}
                        </div>
                      )}
                      <VolumeVocabularyPager {...vocabularyPage} onPage={changeVocabularyPage} />
                    </section>
                  </>
                )}

                <details className="volume-excluded-words">
                  <summary>
                    <span><EyeOff size={17} /> Excluded words</span>
                    <small>{excludedWords.length}</small>
                  </summary>
                  <p>These are hidden from the useful vocabulary list in every volume of {book?.seriesTitle ?? "this manga"}.</p>
                  {excludedWords.length === 0 ? (
                    <span className="volume-excluded-empty">Words you mark “Not useful” will appear here.</span>
                  ) : (
                    <ul>
                      {excludedWords.map((word) => {
                        const key = volumeVocabularyIdentity(word);
                        return (
                          <li key={key}>
                            <span lang="ja"><strong>{word.expression}</strong>{word.reading && <small>{toHiragana(word.reading)}</small>}</span>
                            <button type="button" disabled={exclusionControls.busyKey === key} onClick={() => void exclusionControls.restoreWord(word)}>
                              <Undo2 size={15} /> {exclusionControls.busyKey === key ? "Restoring…" : "Restore"}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </details>
              </>
            )}
          </>
        ) : null}
      </div>
    </main>
  );
}

interface UnsureReviewItem {
  regionId: string;
  text: string;
  bookId: string;
  bookTitle: string;
  seriesTitle: string;
  chapterTitle: string | null;
  pageIndex: number;
  readerUrl: string;
  updatedAt: string;
  translation?: string | null;
  note?: string | null;
}

async function reviewRequest<T>(input: RequestInfo | URL, init?: RequestInit): Promise<T> {
  const response = await fetch(input, init);
  if (!response.ok) {
    const payload = (await response.json().catch(() => null)) as { error?: string } | null;
    throw new Error(payload?.error ?? `Request failed (${response.status})`);
  }
  return response.json() as Promise<T>;
}

async function loadUnsureReview(signal?: AbortSignal): Promise<UnsureReviewItem[]> {
  const payload = await reviewRequest<{ items: UnsureReviewItem[] }>("/api/review/unsure", { signal });
  return payload.items;
}

async function markReviewItemUnderstood(regionId: string): Promise<void> {
  await reviewRequest(`/api/ocr/regions/${encodeURIComponent(regionId)}/comprehension`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ status: "understood" })
  });
}

function ReviewScreen({ theme, onToggleTheme, onLogout }: ThemeProps & { onLogout: () => Promise<void> }) {
  const [items, setItems] = useState<UnsureReviewItem[]>([]);
  const [slowPages, setSlowPages] = useState<PageReadingTimeEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyRegionId, setBusyRegionId] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    Promise.all([loadUnsureReview(controller.signal), loadSlowPageReview(controller.signal)])
      .then(([nextItems, nextSlowPages]) => {
        setItems(nextItems);
        setSlowPages(nextSlowPages);
      })
      .catch((reason) => {
        if (!(reason instanceof DOMException && reason.name === "AbortError")) {
          setMessage(reason instanceof Error ? reason.message : "Could not load your review");
        }
      })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, []);

  const markUnderstood = async (item: UnsureReviewItem) => {
    setBusyRegionId(item.regionId);
    setMessage(null);
    try {
      await markReviewItemUnderstood(item.regionId);
      setItems((current) => current.filter((candidate) => candidate.regionId !== item.regionId));
    } catch (reason) {
      setMessage(reason instanceof Error ? reason.message : "Could not update this line");
    } finally {
      setBusyRegionId(null);
    }
  };

  return (
    <main className={`library-shell review-shell theme-${theme}`}>
      <header className="library-header">
        <Link className="back-link" to="/"><ArrowLeft size={20} /><span>Library</span></Link>
        <div className="vocabulary-header-title">
          <h1>Review</h1>
          <p>{slowPages.length} {slowPages.length === 1 ? "page" : "pages"} · {items.length} {items.length === 1 ? "line" : "lines"}</p>
        </div>
        <div className="header-spacer"><ShellUtilityActions theme={theme} onToggleTheme={onToggleTheme} onLogout={onLogout} /></div>
      </header>
      <div className="library-content review-content">
        <div className="section-heading-row review-heading">
          <div>
            <p className="eyebrow">Reading review</p>
            <h2>Places worth revisiting</h2>
            <p>Pages that took unusually long and lines you marked unsure, together in one place.</p>
          </div>
        </div>
        {message && <p className="vocabulary-message" role="status">{message}</p>}
        {loading ? (
          <div className="vocabulary-loading"><LoaderCircle className="spin" size={22} /> Loading your review…</div>
        ) : items.length === 0 && slowPages.length === 0 ? (
          <section className="empty-vocabulary review-empty">
            <Check size={31} strokeWidth={1.6} />
            <h2>Nothing waiting for review</h2>
            <p>Slower pages and lines you mark unsure while reading will collect here.</p>
            <Link className="primary-button" to="/"><BookOpen size={18} /> Return to library</Link>
          </section>
        ) : (
          <>
            {slowPages.length > 0 && (
              <section className="review-page-section" aria-labelledby="review-slow-pages-title">
                <div className="review-subheading">
                  <div><p className="eyebrow">Reading pace</p><h2 id="review-slow-pages-title">Pages that took longer</h2></div>
                  <p>Average visit time compared with the typical page in that volume.</p>
                </div>
                <div className="review-page-grid">
                  {slowPages.map((page) => (
                    <Link className="review-page-card" to={page.readerUrl} key={page.pageId}>
                      <span className="review-page-thumb"><img src={page.imageUrl} alt="" loading="lazy" /></span>
                      <span className="review-page-copy">
                        <small>{page.seriesTitle} · {page.bookTitle}</small>
                        <strong>Page {page.pageIndex + 1}</strong>
                        <em><Clock3 size={14} /> {formatPageReadingTime(page.averageMs)} average</em>
                        <span>+{formatPageReadingTime(page.deltaMs)} slower</span>
                      </span>
                      <ChevronRight size={18} />
                    </Link>
                  ))}
                </div>
              </section>
            )}
            {items.length > 0 && <div className="review-subheading review-lines-heading"><div><p className="eyebrow">Saved judgement</p><h2>Lines marked unsure</h2></div></div>}
            <div className="review-list">
              {items.map((item) => {
                const busy = busyRegionId === item.regionId;
                const context = [item.seriesTitle, item.bookTitle, item.chapterTitle, `page ${item.pageIndex + 1}`].filter(Boolean).join(" · ");
                return (
                  <article className="review-row" key={item.regionId}>
                  <div className="review-copy">
                    <p className="review-context">{context}</p>
                    <h3 lang="ja">{item.text}</h3>
                    {item.translation && <p className="review-translation">{item.translation}</p>}
                    {item.note && <p className="review-note">{item.note}</p>}
                  </div>
                  <div className="review-actions">
                    <Link className="secondary-button" to={item.readerUrl}>
                      Open in reader <ExternalLink size={15} />
                    </Link>
                    <button className="review-understood-button" type="button" disabled={busy} onClick={() => void markUnderstood(item)}>
                      {busy ? <LoaderCircle className="spin" size={16} /> : <Check size={16} />}
                      {busy ? "Updating…" : "Understood now"}
                    </button>
                  </div>
                  </article>
                );
              })}
            </div>
          </>
        )}
      </div>
    </main>
  );
}

function VocabularyScreen({ theme, onToggleTheme, onLogout }: ThemeProps & { onLogout: () => Promise<void> }) {
  const [words, setWords] = useState<VocabularyWord[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    loadVocabulary(controller.signal)
      .then(setWords)
      .catch((reason) => {
        if (!(reason instanceof DOMException && reason.name === "AbortError")) {
          setMessage(reason instanceof Error ? reason.message : "Could not load saved words");
        }
      })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, []);

  const removeWord = async (word: VocabularyWord) => {
    setMessage(null);
    try {
      await deleteVocabularyWord(word.id);
      setWords((current) => current.filter((item) => item.id !== word.id));
    } catch (reason) {
      setMessage(reason instanceof Error ? reason.message : "Could not remove the saved word");
    }
  };

  return (
    <main className={`library-shell vocabulary-shell theme-${theme}`}>
      <header className="library-header">
        <Link className="back-link" to="/"><ArrowLeft size={20} /><span>Library</span></Link>
        <div className="vocabulary-header-title">
          <h1>Saved words</h1>
          <p>{words.length} {words.length === 1 ? "word" : "words"}</p>
        </div>
        <div className="header-spacer"><ShellUtilityActions theme={theme} onToggleTheme={onToggleTheme} onLogout={onLogout} /></div>
      </header>
      <div className="library-content vocabulary-content">
        <div className="section-heading-row vocabulary-heading">
          <div>
            <p className="eyebrow">Vocabulary</p>
            <h2>Words from your reading</h2>
          </div>
        </div>
        {message && <p className="vocabulary-message" role="status">{message}</p>}
        {loading ? (
          <div className="vocabulary-loading"><LoaderCircle className="spin" size={22} /> Loading saved words…</div>
        ) : words.length === 0 ? (
          <section className="empty-vocabulary">
            <Bookmark size={30} strokeWidth={1.5} />
            <h2>No saved words yet</h2>
            <p>While reading, select Japanese text and save any word you want to remember.</p>
            <Link className="primary-button" to="/"><BookOpen size={18} /> Return to library</Link>
          </section>
        ) : (
          <div className="vocabulary-list">
            {words.map((word) => (
              <article className="vocabulary-row" key={word.id}>
                <div className="vocabulary-context-thumb">
                  {word.contextImageUrl && word.readerUrl ? (
                    <Link to={word.readerUrl} aria-label={`Open ${word.expression} in the manga`}>
                      <img src={word.contextImageUrl} alt="" loading="lazy" />
                    </Link>
                  ) : <Bookmark size={24} strokeWidth={1.4} aria-hidden="true" />}
                </div>
                <div className="vocabulary-expression" lang="ja">
                  <strong>{word.expression}</strong>
                  {word.reading && <span>{toHiragana(word.reading)}</span>}
                </div>
                <div className="vocabulary-definition">
                  <p>{word.definition || "No definition saved"}</p>
                  <span>{[word.seriesTitle, word.chapterTitle, word.pageIndex === null ? null : `page ${word.pageIndex + 1}`].filter(Boolean).join(" · ")}</span>
                  {word.sentence && <blockquote lang="ja">{word.sentence}</blockquote>}
                  {word.translation && <p className="vocabulary-translation">{word.translation}</p>}
                  {word.note && <p className="vocabulary-note">{word.note}</p>}
                  {word.readerUrl && (
                    <Link className="vocabulary-open-link" to={word.readerUrl}>
                      Open in manga <ExternalLink size={14} />
                    </Link>
                  )}
                </div>
                <button className="remove-word-button" type="button" onClick={() => void removeWord(word)} aria-label={`Remove ${word.expression} from saved words`}>
                  <Trash2 size={18} />
                </button>
              </article>
            ))}
          </div>
        )}
        <footer className="dictionary-attribution">
          Dictionary data from <a href="https://www.edrdg.org/wiki/index.php/JMdict-EDICT_Dictionary_Project" target="_blank" rel="noreferrer">JMdict</a> and <a href="https://www.edrdg.org/wiki/KANJIDIC_Project.html" target="_blank" rel="noreferrer">KANJIDIC2</a>, maintained by EDRDG and licensed under CC BY-SA 4.0.
        </footer>
      </div>
    </main>
  );
}

function Cover({ book, eager = false }: { book: LibraryBook; eager?: boolean }) {
  return (
    <div className="cover-frame">
      {book.coverUrl ? (
        <img src={book.coverUrl} alt="" loading={eager ? "eager" : "lazy"} />
      ) : (
        <BookOpen aria-hidden="true" />
      )}
    </div>
  );
}

function ProgressBar({ value }: { value: number }) {
  const percentage = Math.min(value * 100, 100);
  return (
    <span className="progress-track" aria-label={`${Math.round(value * 100)}% read`}>
      {percentage > 0 && <span style={{ width: `${Math.max(2, percentage)}%` }} />}
    </span>
  );
}

function bookProgress(book: LibraryBook): number {
  if (!book.progressUpdatedAt || book.pageCount === 0) return 0;
  return (book.currentPage + 1) / book.pageCount;
}

type MonthSelection = {
  year: number;
  month: number;
  label: string;
  daysInMonth: number;
  key: string;
};

function monthIdentifierForDate(date: Date): string {
  const safeDate = Number.isFinite(date.getTime()) ? date : new Date();
  const year = safeDate.getFullYear();
  const month = safeDate.getMonth() + 1;
  return `${year}-${String(month).padStart(2, "0")}`;
}

function monthLabelFor(year: number, month: number): string {
  return new Date(year, month, 1).toLocaleDateString(undefined, {
    month: "long",
    year: "numeric"
  });
}

function monthDayId(year: number, month: number, day: number): string {
  return `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

function monthRangeFromIdentifier(month: MonthSelection): { from: string; to: string } {
  const from = new Date(month.year, month.month, 1).toISOString();
  const to = new Date(month.year, month.month + 1, 1).toISOString();
  return { from, to };
}

function readingHabitContextForMonth(month: MonthSelection, now = new Date()): {
  from: string;
  to: string;
  asOfDay: string;
  label: string;
  historical: boolean;
} {
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const selectedMonthEnd = new Date(month.year, month.month + 1, 0);
  const historical = selectedMonthEnd.getTime() < today.getTime();
  const anchor = historical ? selectedMonthEnd : today;
  const from = new Date(anchor.getFullYear(), anchor.getMonth(), anchor.getDate() - 368);
  const to = new Date(anchor.getFullYear(), anchor.getMonth(), anchor.getDate() + 1);
  return {
    from: from.toISOString(),
    to: to.toISOString(),
    asOfDay: monthDayId(anchor.getFullYear(), anchor.getMonth(), anchor.getDate()),
    label: historical
      ? `Through ${anchor.toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" })}`
      : "Up to today",
    historical
  };
}

function readingHabitDayLabel(day: string): string {
  const date = new Date(`${day}T12:00:00`);
  return Number.isFinite(date.getTime())
    ? date.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" })
    : day;
}

function parseMonthIdentifier(input: string | null, fallback: Date = new Date()): MonthSelection {
  const fallbackDate = Number.isFinite(fallback.getTime()) ? fallback : new Date();
  const fallbackMonth = {
    year: fallbackDate.getFullYear(),
    month: fallbackDate.getMonth()
  };
  const defaultKey = monthIdentifierForDate(fallbackDate);
  const match = input?.trim().match(/^(\d{4})-(\d{2})$/u);
  if (!match) {
    return {
      year: fallbackMonth.year,
      month: fallbackMonth.month,
      label: monthLabelFor(fallbackMonth.year, fallbackMonth.month),
      daysInMonth: new Date(fallbackMonth.year, fallbackMonth.month + 1, 0).getDate(),
      key: defaultKey
    };
  }
  const year = Number(match[1]);
  const month = Number(match[2]) - 1;
  if (!Number.isFinite(year) || !Number.isInteger(month) || month < 0 || month > 11) {
    return {
      year: fallbackMonth.year,
      month: fallbackMonth.month,
      label: monthLabelFor(fallbackMonth.year, fallbackMonth.month),
      daysInMonth: new Date(fallbackMonth.year, fallbackMonth.month + 1, 0).getDate(),
      key: defaultKey
    };
  }
  return {
    year,
    month,
    label: monthLabelFor(year, month),
    daysInMonth: new Date(year, month + 1, 0).getDate(),
    key: monthIdentifierForDate(new Date(year, month, 1))
  };
}

function formatReadingTime(milliseconds: number): string {
  if (milliseconds <= 0) return "0 min";
  const minutes = Math.floor(milliseconds / 60_000);
  if (minutes < 1) return "<1 min";
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  return remainder === 0 ? `${hours}h` : `${hours}h ${remainder}m`;
}

function formatPageReadingTime(milliseconds: number): string {
  const seconds = Math.max(0, Math.round(milliseconds / 1_000));
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  return remainder === 0 ? `${minutes}m` : `${minutes}m ${remainder}s`;
}

function LibraryLoading() {
  return (
    <section className="library-loading" aria-label="Loading library">
      <div className="loading-cover" />
      <div className="loading-lines"><span /><span /><span /></div>
    </section>
  );
}

function chapterForPage(book: LibraryBook): string {
  if (book.chapterCount <= 1) return book.title;
  return `${book.chapterCount} chapters`;
}

type LayoutMode = "auto" | "single" | "spread";

function useReaderReadingTime(
  bookId: string | undefined,
  pageId: string | undefined,
  active: boolean,
  onRecorded?: (event: ReadingTimeSegment) => void
) {
  const recorderRef = useRef<ReadingTimeRecorder | null>(null);
  const send = useCallback<SendReadingTimeEvent>(async (targetBookId, event, options) => {
    const recorded = await recordReadingTime(targetBookId, event, options);
    if (recorded) onRecorded?.(event);
  }, [onRecorded]);

  useEffect(() => {
    if (!bookId) return;
    const recorder = new ReadingTimeRecorder(bookId, { outbox: getSharedReadingTimeOutbox(), pageId });
    recorderRef.current = recorder;
    void recorder.replay(send);

    const persistNow = () => {
      recorder.pause();
      void recorder.replay(send, { keepalive: true });
    };
    window.addEventListener("pagehide", persistNow);
    return () => {
      window.removeEventListener("pagehide", persistNow);
      persistNow();
      if (recorderRef.current === recorder) recorderRef.current = null;
    };
  }, [bookId, send]);

  useEffect(() => {
    const recorder = recorderRef.current;
    if (!recorder || recorder.session.bookId !== bookId) return;
    recorder.setPage(pageId);
    void recorder.replay(send, { keepalive: true });

    const sync = () => {
      if (active && document.visibilityState === "visible") {
        recorder.start();
      } else {
        recorder.pause();
        void recorder.replay(send, { keepalive: true });
      }
    };
    const heartbeat = () => {
      if (!recorder.active) return;
      recorder.heartbeat();
      void recorder.replay(send);
    };

    sync();
    document.addEventListener("visibilitychange", sync);
    window.addEventListener("pageshow", sync);
    const timer = window.setInterval(heartbeat, 15_000);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", sync);
      window.removeEventListener("pageshow", sync);
      recorder.pause();
      void recorder.replay(send, { keepalive: true });
    };
  }, [active, bookId, pageId, send]);
}

function useActivePageVisitTime(pageId: string | undefined, active: boolean): number {
  const clockRef = useRef<{ pageId: string | undefined; elapsedMs: number; startedAt: number | null }>({
    pageId,
    elapsedMs: 0,
    startedAt: null
  });
  const [displayMs, setDisplayMs] = useState(0);

  useEffect(() => {
    const clock = clockRef.current;
    if (clock.pageId !== pageId) {
      clock.pageId = pageId;
      clock.elapsedMs = 0;
      clock.startedAt = null;
      setDisplayMs(0);
    }
    const update = () => {
      const now = performance.now();
      const value = clock.elapsedMs + (clock.startedAt === null ? 0 : Math.max(0, now - clock.startedAt));
      setDisplayMs(Math.floor(value));
    };
    const sync = () => {
      const shouldRun = active && document.visibilityState === "visible";
      if (shouldRun && clock.startedAt === null) clock.startedAt = performance.now();
      if (!shouldRun && clock.startedAt !== null) {
        clock.elapsedMs += Math.max(0, performance.now() - clock.startedAt);
        clock.startedAt = null;
      }
      update();
    };
    sync();
    document.addEventListener("visibilitychange", sync);
    window.addEventListener("pageshow", sync);
    const timer = window.setInterval(update, 1_000);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", sync);
      window.removeEventListener("pageshow", sync);
      if (clock.startedAt !== null) {
        clock.elapsedMs += Math.max(0, performance.now() - clock.startedAt);
        clock.startedAt = null;
      }
      setDisplayMs(Math.floor(clock.elapsedMs));
    };
  }, [active, pageId]);

  return clockRef.current.pageId === pageId ? displayMs : 0;
}

function ReaderPaceDrawer({
  book,
  page,
  visitMs,
  onClose
}: {
  book: BookDetail;
  page: MangaPage;
  visitMs: number;
  onClose: () => void;
}) {
  const [summary, setSummary] = useState<PageReadingTimeSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<string | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const drawerRef = useRef<HTMLElement>(null);

  const refresh = useCallback(() => {
    const controller = new AbortController();
    setLoading(true);
    loadPageReadingTime(book.id, page.id, controller.signal)
      .then((next) => {
        setSummary(next);
        setMessage(null);
      })
      .catch((reason) => {
        if (!(reason instanceof DOMException && reason.name === "AbortError")) {
          setMessage(reason instanceof Error ? reason.message : "Could not load page timing");
        }
      })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, [book.id, page.id]);

  useEffect(() => closeButtonRef.current?.focus(), []);
  useEffect(() => refresh(), [refresh]);

  const typicalMs = summary?.typicalMs ?? null;
  const deltaMs = typicalMs === null ? null : visitMs - typicalMs;
  const meaningfulDelta = deltaMs !== null && Math.abs(deltaMs) >= 5_000;
  const deltaLabel = typicalMs === null
    ? "Building a volume baseline"
    : !meaningfulDelta
      ? "Around your usual pace"
      : `${formatPageReadingTime(Math.abs(deltaMs))} ${deltaMs > 0 ? "slower" : "faster"} than usual`;

  return (
    <>
      <button className="reader-vocabulary-backdrop" type="button" data-reader-control tabIndex={-1} aria-hidden="true" onClick={onClose} />
      <aside
        ref={drawerRef}
        id="reader-pace-drawer"
        className="reader-vocabulary-drawer reader-pace-drawer"
        data-reader-control
        role="dialog"
        aria-modal="true"
        aria-labelledby="reader-pace-title"
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            event.preventDefault();
            event.stopPropagation();
            onClose();
          } else if (event.key === "Tab") {
            const focusable = Array.from(drawerRef.current?.querySelectorAll<HTMLElement>(
              "a[href], button:not([disabled]), [tabindex]:not([tabindex='-1'])"
            ) ?? []);
            const first = focusable[0];
            const last = focusable.at(-1);
            if (!first || !last) return;
            if (event.shiftKey && document.activeElement === first) {
              event.preventDefault();
              last.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
              event.preventDefault();
              first.focus();
            }
          }
        }}
      >
        <header className="reader-vocabulary-heading">
          <div>
            <p className="eyebrow">Reading pace</p>
            <h2 id="reader-pace-title">Page review</h2>
            <span>{book.title}</span>
          </div>
          <button ref={closeButtonRef} type="button" onClick={onClose} aria-label="Close page review"><X size={20} /></button>
        </header>

        <section className="reader-pace-live" aria-label="Current page reading time">
          <div className="reader-pace-live-heading">
            <span><Clock3 size={17} /> This visit · page {page.index + 1}</span>
            {summary?.trackedPages ? <small>{summary.trackedPages} pages tracked</small> : null}
          </div>
          <strong aria-live="polite">{formatPageReadingTime(visitMs)}</strong>
          <p className={deltaMs !== null && deltaMs > 0 && meaningfulDelta ? "is-slower" : ""}>{deltaLabel}</p>
          {typicalMs !== null && (
            <div className="reader-pace-baseline">
              <span>Volume typical</span><b>{formatPageReadingTime(typicalMs)}</b>
            </div>
          )}
        </section>

        <section className="reader-pace-review" aria-labelledby="reader-pace-review-title">
          <div className="reader-pace-section-heading">
            <div>
              <p className="eyebrow">Worth another look</p>
              <h3 id="reader-pace-review-title">Slower pages</h3>
            </div>
            <span>Compared with this volume</span>
          </div>
          {loading && !summary ? (
            <div className="reader-vocabulary-status" role="status"><LoaderCircle className="spin" size={20} /> Reading the pattern…</div>
          ) : message && !summary ? (
            <div className="reader-vocabulary-status is-error" role="alert"><span>{message}</span><button type="button" onClick={refresh}>Try again</button></div>
          ) : summary?.slowerPages.length ? (
            <div className="reader-pace-grid">
              {summary.slowerPages.map((item) => (
                <Link className="reader-pace-card" to={item.readerUrl} onClick={onClose} key={item.pageId}>
                  <span className="reader-pace-thumbnail"><img src={item.imageUrl} alt="" loading="lazy" /></span>
                  <span className="reader-pace-card-copy">
                    <strong>Page {item.pageIndex + 1}</strong>
                    <small>{formatPageReadingTime(item.averageMs)} average</small>
                    <em>+{formatPageReadingTime(item.deltaMs)} slower</em>
                  </span>
                  <ChevronRight size={17} />
                </Link>
              ))}
            </div>
          ) : (
            <div className="reader-pace-empty">
              <Check size={22} />
              <p><strong>No unusually slow pages yet.</strong><span>Once at least four pages have timing data, difficult-looking outliers will collect here.</span></p>
            </div>
          )}
        </section>

        <Link className="reader-vocabulary-full-link" to="/review" onClick={onClose}>
          Open the full review area <ChevronRight size={17} />
        </Link>
      </aside>
    </>
  );
}

function ReaderScreen() {
  const { bookId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const requestedPage = searchParams.get("page");
  const requestedRegion = searchParams.get("region");
  const prefersSpread = useMediaQuery("(min-width: 980px) and (orientation: landscape)");
  const [book, setBook] = useState<BookDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [positionReady, setPositionReady] = useState(false);
  const [chromeVisible, setChromeVisible] = useState(true);
  const [pageIndicatorVisible, setPageIndicatorVisible] = useState(false);
  const [goalCelebrations, setGoalCelebrations] = useState<ReadingGoalCelebration[]>([]);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [vocabularyOpen, setVocabularyOpen] = useState(false);
  const [assistEnabled, setAssistEnabled] = useState(true);
  const [ocrRegions, setOcrRegions] = useState<OcrRegion[]>([]);
  const [ocrRefreshingPageIds, setOcrRefreshingPageIds] = useState<ReadonlySet<string>>(() => new Set());
  const [ocrRefreshRevision, setOcrRefreshRevision] = useState(0);
  const [supportState, setSupportState] = useState<SupportState>({ status: "idle" });
  const [replacementRegion, setReplacementRegion] = useState<OcrRegion | null>(null);
  const [recognitionInFlight, setRecognitionInFlight] = useState(false);
  const [supportPanelBounds, setSupportPanelBounds] = useState<SupportPanelBounds | null>(null);
  const [guidedViewOpen, setGuidedViewOpen] = useState(false);
  const [guidedPanels, setGuidedPanels] = useState<MangaPanel[]>([]);
  const [guidedPanelIndex, setGuidedPanelIndex] = useState(0);
  const [guidedPanelLoading, setGuidedPanelLoading] = useState(false);
  const [guidedPanelMessage, setGuidedPanelMessage] = useState<string | null>(null);
  const guidedPageEntry = useRef<"first" | "last">("first");
  const [readyPages, setReadyPages] = useState<{ groupKey: string; pageIds: Set<string> }>({ groupKey: "", pageIds: new Set() });
  const recognitionInFlightRef = useRef(false);
  const ocrRefreshingPageIdsRef = useRef<ReadonlySet<string>>(new Set());
  const recognitionPresentation = useRef(0);
  const [layoutMode, setLayoutMode] = useState<LayoutMode>(() => {
    const saved = window.localStorage.getItem("manga-reader-layout");
    return saved === "single" || saved === "spread" ? saved : "auto";
  });
  const pointerStart = useRef<{
    pointerId: number;
    x: number;
    y: number;
    time: number;
    axis: "pending" | "horizontal" | "vertical";
  } | null>(null);
  const pagesRef = useRef<HTMLDivElement>(null);
  const hideTimer = useRef<number | null>(null);
  const pageIndicatorTimer = useRef<number | null>(null);
  const pageIndicatorBook = useRef<string | null>(null);
  const pageIndicatorPage = useRef<number | null>(null);
  const readingGoalsDashboard = useRef<ReadingGoalsDashboard | null>(null);
  const readingTimeDeliveryRevision = useRef(0);
  const vocabularyButtonRef = useRef<HTMLButtonElement>(null);
  const deepLinkHandled = useRef(false);

  const updateSupportPanelBounds = useCallback((nextBounds: SupportPanelBounds | null) => {
    setSupportPanelBounds((current) => {
      if (current === null || nextBounds === null) return current === nextBounds ? current : nextBounds;
      const unchanged = Math.abs(current.top - nextBounds.top) < 0.5
        && Math.abs(current.right - nextBounds.right) < 0.5
        && Math.abs(current.bottom - nextBounds.bottom) < 0.5
        && Math.abs(current.left - nextBounds.left) < 0.5
        && Math.abs(current.viewportHeight - nextBounds.viewportHeight) < 0.5;
      return unchanged ? current : nextBounds;
    });
  }, []);

  const closeVocabulary = useCallback(() => {
    setVocabularyOpen(false);
    setChromeVisible(true);
    window.requestAnimationFrame(() => vocabularyButtonRef.current?.focus());
  }, []);

  useEffect(() => {
    deepLinkHandled.current = false;
  }, [bookId, requestedRegion]);

  const spreadEnabled = layoutMode === "spread" || (layoutMode === "auto" && prefersSpread);
  const isRtl = book?.readingDirection !== "ltr";

  useEffect(() => {
    if (!bookId) return;
    const controller = new AbortController();
    setPositionReady(false);
    loadBook(bookId, controller.signal)
      .then((loaded) => {
        const linkedPage = requestedPage !== null && /^\d+$/u.test(requestedPage) ? Number.parseInt(requestedPage, 10) : null;
        setCurrentPage(linkedPage !== null && linkedPage < loaded.pageCount ? linkedPage : loaded.currentPage);
        setBook(loaded);
        setPositionReady(true);
        document.title = `${loaded.seriesTitle} · メクリ`;
      })
      .catch((reason) => {
        if (!(reason instanceof DOMException && reason.name === "AbortError")) {
          setError(reason instanceof Error ? reason.message : "Could not open this manga");
        }
      });
    return () => controller.abort();
  }, [bookId, requestedPage]);

  const layoutGroups = useMemo(() => createLayoutGroups(book?.pages ?? [], spreadEnabled), [book?.pages, spreadEnabled]);
  const groupIndex = Math.max(0, layoutGroups.findIndex((group) => group.some((page) => page.index === currentPage)));
  const currentGroup = layoutGroups[groupIndex] ?? [];
  const currentGroupKey = currentGroup.map((page) => page.id).join(":");
  const currentGroupReady = currentGroup.length > 0
    && readyPages.groupKey === currentGroupKey
    && currentGroup.every((page) => readyPages.pageIds.has(page.id));
  const activePage = book?.pages[currentPage] ?? currentGroup[0];
  const supportPage = supportState.status === "done"
    ? book?.pages.find((page) => page.id === supportState.region.pageId) ?? activePage
    : activePage;
  const supportRegions = supportPage ? ocrRegions.filter((region) => region.pageId === supportPage.id) : [];
  const guidedPanel = guidedPanels[guidedPanelIndex];
  const supportLandscapeSide = supportState.status === "done"
    ? oppositeSupportSide(guidedViewOpen ? guidedPanel : undefined, supportState.region)
    : "right";

  useEffect(() => {
    if (!guidedViewOpen || !activePage || !book) return;
    let active = true;
    const entry = guidedPageEntry.current;
    guidedPageEntry.current = "first";
    setGuidedPanelLoading(true);
    setGuidedPanelMessage(null);
    loadMangaPanels(activePage.imageUrl, book.readingDirection)
      .then((panels) => {
        if (!active) return;
        setGuidedPanels(panels);
        setGuidedPanelIndex(entry === "last" ? Math.max(0, panels.length - 1) : 0);
      })
      .catch(() => {
        if (!active) return;
        setGuidedPanels([{ x: 0, y: 0, width: 1, height: 1 }]);
        setGuidedPanelIndex(0);
        setGuidedPanelMessage("This page has no clear gutters, so Guided View is showing it as one frame.");
      })
      .finally(() => {
        if (active) setGuidedPanelLoading(false);
      });
    return () => {
      active = false;
    };
  }, [activePage, book, guidedViewOpen]);
  const handleRecordedReadingTime = useCallback((event: ReadingTimeSegment) => {
    readingTimeDeliveryRevision.current += 1;
    const dashboard = readingGoalsDashboard.current;
    if (!dashboard) return;
    const update = applyReadingTimeToGoals(dashboard, event);
    readingGoalsDashboard.current = update.dashboard;
    if (update.celebrations.length === 0) return;
    setGoalCelebrations((current) => {
      const queued = new Set(current.map((celebration) => `${celebration.kind}:${celebration.periodFrom}`));
      return [
        ...current,
        ...update.celebrations.filter((celebration) => !queued.has(`${celebration.kind}:${celebration.periodFrom}`))
      ];
    });
  }, []);
  const readingActive = Boolean(book && positionReady && currentGroupReady && !vocabularyOpen);
  const pageVisitMs = useActivePageVisitTime(activePage?.id, readingActive);
  useReaderReadingTime(
    bookId,
    activePage?.id,
    readingActive,
    handleRecordedReadingTime
  );

  useEffect(() => {
    if (!bookId) return;
    const controller = new AbortController();
    let active = true;
    let boundaryTimer: number | undefined;
    let refreshRequest = 0;
    readingGoalsDashboard.current = null;
    readingTimeDeliveryRevision.current = 0;
    setGoalCelebrations([]);

    const refreshGoals = async () => {
      const request = ++refreshRequest;
      let deliveredRevision: number;
      let dashboard: ReadingGoalsDashboard;
      do {
        deliveredRevision = readingTimeDeliveryRevision.current;
        const ranges = currentReadingGoalRanges();
        dashboard = await loadReadingGoalsDashboard(ranges.day, ranges.week, controller.signal);
      } while (active && deliveredRevision !== readingTimeDeliveryRevision.current);
      if (!active || controller.signal.aborted || request !== refreshRequest) return;
      readingGoalsDashboard.current = dashboard;
    };
    const refresh = () => {
      void refreshGoals().catch((reason) => {
        if (active && !(reason instanceof DOMException && reason.name === "AbortError")) {
          readingGoalsDashboard.current = null;
        }
      });
    };
    const scheduleBoundaryRefresh = () => {
      if (boundaryTimer !== undefined) window.clearTimeout(boundaryTimer);
      const now = new Date();
      const nextDay = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
      boundaryTimer = window.setTimeout(() => {
        refresh();
        scheduleBoundaryRefresh();
      }, Math.max(1_000, nextDay.getTime() - now.getTime() + 100));
    };
    const resume = () => {
      if (document.visibilityState !== "visible") return;
      refresh();
      scheduleBoundaryRefresh();
    };

    refresh();
    scheduleBoundaryRefresh();
    document.addEventListener("visibilitychange", resume);
    window.addEventListener("pageshow", resume);
    return () => {
      active = false;
      controller.abort();
      readingGoalsDashboard.current = null;
      document.removeEventListener("visibilitychange", resume);
      window.removeEventListener("pageshow", resume);
      if (boundaryTimer !== undefined) window.clearTimeout(boundaryTimer);
    };
  }, [bookId]);

  const activeGoalCelebration = goalCelebrations[0] ?? null;
  useEffect(() => {
    if (!activeGoalCelebration) return;
    const timer = window.setTimeout(() => {
      setGoalCelebrations((current) => current.slice(1));
    }, 4_800);
    return () => window.clearTimeout(timer);
  }, [activeGoalCelebration]);

  const markPageReady = useCallback((pageId: string) => {
    setReadyPages((current) => {
      const pageIds = current.groupKey === currentGroupKey ? new Set(current.pageIds) : new Set<string>();
      pageIds.add(pageId);
      return { groupKey: currentGroupKey, pageIds };
    });
  }, [currentGroupKey]);

  const revealChrome = useCallback(() => {
    setChromeVisible(true);
    if (hideTimer.current) window.clearTimeout(hideTimer.current);
    hideTimer.current = window.setTimeout(() => {
      if (!settingsOpen && !vocabularyOpen && !assistEnabled && !replacementRegion && supportState.status === "idle") setChromeVisible(false);
    }, 3200);
  }, [assistEnabled, replacementRegion, settingsOpen, supportState.status, vocabularyOpen]);

  const hideChrome = useCallback(() => {
    if (hideTimer.current) window.clearTimeout(hideTimer.current);
    hideTimer.current = null;
    setChromeVisible(false);
  }, []);

  useEffect(() => {
    if (!book) return;
    revealChrome();
    return () => {
      if (hideTimer.current) window.clearTimeout(hideTimer.current);
    };
  }, [book?.id, revealChrome]);

  const moveToGroup = useCallback((nextGroupIndex: number) => {
    const nextGroup = layoutGroups[Math.max(0, Math.min(nextGroupIndex, layoutGroups.length - 1))];
    if (!nextGroup) return;
    setCurrentPage(nextGroup[0].index);
    ++recognitionPresentation.current;
    setSupportState({ status: "idle" });
    setReplacementRegion(null);
    if (chromeVisible) revealChrome();
  }, [chromeVisible, layoutGroups, revealChrome]);

  const next = useCallback(() => moveToGroup(groupIndex + 1), [groupIndex, moveToGroup]);
  const previous = useCallback(() => moveToGroup(groupIndex - 1), [groupIndex, moveToGroup]);

  const openGuidedView = useCallback(() => {
    guidedPageEntry.current = "first";
    setGuidedPanels([]);
    setGuidedPanelIndex(0);
    setGuidedPanelMessage(null);
    setSettingsOpen(false);
    setVocabularyOpen(false);
    setReplacementRegion(null);
    setSupportState({ status: "idle" });
    setGuidedViewOpen(true);
    setChromeVisible(false);
  }, []);

  const closeGuidedView = useCallback(() => {
    ++recognitionPresentation.current;
    setSupportState({ status: "idle" });
    setReplacementRegion(null);
    setGuidedViewOpen(false);
    setChromeVisible(true);
  }, []);

  const moveToGuidedPage = useCallback((pageIndex: number, entry: "first" | "last") => {
    if (!book || pageIndex < 0 || pageIndex >= book.pages.length) return;
    guidedPageEntry.current = entry;
    setGuidedPanels([]);
    setGuidedPanelIndex(0);
    setGuidedPanelMessage(null);
    ++recognitionPresentation.current;
    setSupportState({ status: "idle" });
    setReplacementRegion(null);
    setCurrentPage(pageIndex);
  }, [book]);

  const nextGuidedPanel = useCallback(() => {
    if (guidedPanelIndex < guidedPanels.length - 1) {
      ++recognitionPresentation.current;
      setSupportState({ status: "idle" });
      setReplacementRegion(null);
      setGuidedPanelIndex((index) => index + 1);
      return;
    }
    moveToGuidedPage(currentPage + 1, "first");
  }, [currentPage, guidedPanelIndex, guidedPanels.length, moveToGuidedPage]);

  const previousGuidedPanel = useCallback(() => {
    if (guidedPanelIndex > 0) {
      ++recognitionPresentation.current;
      setSupportState({ status: "idle" });
      setReplacementRegion(null);
      setGuidedPanelIndex((index) => index - 1);
      return;
    }
    moveToGuidedPage(currentPage - 1, "last");
  }, [currentPage, guidedPanelIndex, moveToGuidedPage]);

  useEffect(() => {
    if (!book || !positionReady) return;
    if (pageIndicatorBook.current !== book.id) {
      pageIndicatorBook.current = book.id;
      pageIndicatorPage.current = currentPage;
      setPageIndicatorVisible(false);
      return;
    }
    if (pageIndicatorPage.current === currentPage) return;
    pageIndicatorPage.current = currentPage;
    if (pageIndicatorTimer.current) window.clearTimeout(pageIndicatorTimer.current);
    setPageIndicatorVisible(true);
    pageIndicatorTimer.current = window.setTimeout(() => {
      pageIndicatorTimer.current = null;
      setPageIndicatorVisible(false);
    }, 2200);
    return () => {
      if (pageIndicatorTimer.current) window.clearTimeout(pageIndicatorTimer.current);
    };
  }, [book, currentPage, positionReady]);

  useEffect(() => {
    if (!bookId || !book || !positionReady || !currentGroupReady) return;
    const timer = window.setTimeout(() => void saveProgress(
      bookId,
      currentPage,
      groupIndex === layoutGroups.length - 1
    ), 350);
    return () => window.clearTimeout(timer);
  }, [book, bookId, currentGroupReady, currentPage, groupIndex, layoutGroups.length, positionReady]);

  useEffect(() => {
    const pagesToPrefetch = layoutGroups.slice(groupIndex + 1, groupIndex + 3).flat();
    for (const page of pagesToPrefetch) {
      const image = new Image();
      image.src = page.imageUrl;
    }
  }, [groupIndex, layoutGroups]);

  useEffect(() => {
    const pageIds = currentGroup.map((page) => page.id);
    if (pageIds.length === 0) return;
    const controller = new AbortController();
    let refreshTimer: number | undefined;
    const pendingPages = new Set(pageIds);
    ocrRefreshingPageIdsRef.current = pendingPages;
    setOcrRefreshingPageIds(pendingPages);
    loadOcrRegions(pageIds, controller.signal)
      .then(({ regions: loadedRegions, refreshingPageIds }) => {
        const refreshingPages = new Set(refreshingPageIds);
        ocrRefreshingPageIdsRef.current = refreshingPages;
        setOcrRefreshingPageIds(refreshingPages);
        setOcrRegions(loadedRegions);
        if (refreshingPageIds.length > 0) {
          ++recognitionPresentation.current;
          setReplacementRegion(null);
          setSupportState((current) => current.status === "scanning" || (current.status === "done" && refreshingPages.has(current.region.pageId))
            ? { status: "idle" }
            : current);
          refreshTimer = window.setTimeout(() => setOcrRefreshRevision((revision) => revision + 1), 1_500);
        }
        if (!deepLinkHandled.current && requestedRegion) {
          const linkedRegion = loadedRegions.find((region) => region.id === requestedRegion);
          if (linkedRegion) {
            deepLinkHandled.current = true;
            setAssistEnabled(true);
            setChromeVisible(true);
            setSupportState({
              status: "done",
              region: linkedRegion,
              groupedRegions: contourGroupForRegion(loadedRegions, linkedRegion)
            });
          }
        }
      })
      .catch((reason) => {
        if (!(reason instanceof DOMException && reason.name === "AbortError")) {
          ocrRefreshingPageIdsRef.current = new Set();
          setOcrRefreshingPageIds(new Set());
          console.error(reason);
        }
      });
    return () => {
      controller.abort();
      if (refreshTimer !== undefined) window.clearTimeout(refreshTimer);
    };
  }, [currentGroup.map((page) => page.id).join(","), ocrRefreshRevision, requestedRegion]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (guidedViewOpen) return;
      const target = event.target instanceof Element ? event.target : null;
      if (target?.closest("input, select, textarea, button, a, [contenteditable='true'], [role='textbox']")) return;
      const nextArrow = isRtl ? "ArrowLeft" : "ArrowRight";
      const previousArrow = isRtl ? "ArrowRight" : "ArrowLeft";
      if (event.key === nextArrow || event.key === "PageDown" || (event.key === " " && !event.shiftKey)) {
        event.preventDefault();
        next();
      } else if (event.key === previousArrow || event.key === "PageUp" || (event.key === " " && event.shiftKey)) {
        event.preventDefault();
        previous();
      } else if (event.key === "Escape") {
        if (vocabularyOpen) {
          closeVocabulary();
          return;
        }
        setSettingsOpen(false);
        setReplacementRegion(null);
        hideChrome();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [closeVocabulary, guidedViewOpen, hideChrome, isRtl, next, previous, vocabularyOpen]);

  const handlePointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!event.isPrimary || event.button !== 0 || (event.target as HTMLElement).closest("[data-reader-control]")) return;
    pointerStart.current = {
      pointerId: event.pointerId,
      x: event.clientX,
      y: event.clientY,
      time: performance.now(),
      axis: "pending"
    };
    event.currentTarget.setPointerCapture(event.pointerId);
    pagesRef.current?.classList.add("is-swiping");
  };

  const resetSwipePosition = () => {
    const pages = pagesRef.current;
    if (!pages) return;
    pages.classList.remove("is-swiping");
    pages.style.setProperty("--reader-swipe-x", "0px");
  };

  const handlePointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const gesture = pointerStart.current;
    if (!gesture || gesture.pointerId !== event.pointerId) return;
    const deltaX = event.clientX - gesture.x;
    const deltaY = event.clientY - gesture.y;
    if (gesture.axis === "pending" && Math.max(Math.abs(deltaX), Math.abs(deltaY)) >= 10) {
      gesture.axis = Math.abs(deltaX) > Math.abs(deltaY) * 1.12 ? "horizontal" : "vertical";
    }
    if (gesture.axis !== "horizontal") return;
    event.preventDefault();
    const limit = Math.min(window.innerWidth * 0.2, 180);
    const resistedOffset = limit * Math.tanh(deltaX / limit);
    pagesRef.current?.style.setProperty("--reader-swipe-x", `${resistedOffset}px`);
  };

  const handlePointerUp = (event: ReactPointerEvent<HTMLDivElement>) => {
    const start = pointerStart.current;
    pointerStart.current = null;
    if (!start || start.pointerId !== event.pointerId) return;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    const deltaX = event.clientX - start.x;
    const deltaY = event.clientY - start.y;
    const duration = performance.now() - start.time;
    resetSwipePosition();
    if (isPageTurnSwipe({
      deltaX,
      deltaY,
      durationMs: duration,
      viewportWidth: window.innerWidth,
      horizontalLocked: start.axis === "horizontal"
    })) {
      if ((deltaX > 0) === isRtl) next();
      else previous();
      return;
    }
    if (Math.abs(deltaX) > 12 || Math.abs(deltaY) > 12) return;
    const ratio = event.clientX / window.innerWidth;
    if (ratio < 0.2) (isRtl ? next : previous)();
    else if (ratio > 0.8) (isRtl ? previous : next)();
    else if (chromeVisible) hideChrome();
    else revealChrome();
  };

  const handlePointerCancel = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (pointerStart.current?.pointerId !== event.pointerId) return;
    pointerStart.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    resetSwipePosition();
  };

  const changeLayout = (mode: LayoutMode) => {
    setLayoutMode(mode);
    window.localStorage.setItem("manga-reader-layout", mode);
    setSettingsOpen(false);
  };

  const recognizeSelection = async (page: MangaPage, region: NormalizedRegion) => {
    if (ocrRefreshingPageIdsRef.current.has(page.id) || recognitionInFlightRef.current) return;
    recognitionInFlightRef.current = true;
    setRecognitionInFlight(true);
    const presentationId = ++recognitionPresentation.current;
    const replacedRegionId = replacementRegion?.id;
    setSupportState({ status: "scanning", warming: false });
    setChromeVisible(true);
    try {
      const recognition = recognizePageRegion(page.id, region, replacedRegionId);
      const ocrStatus = await loadOcrStatus().catch(() => null);
      if (presentationId === recognitionPresentation.current && ocrStatus && !ocrStatus.ready) {
        setSupportState({ status: "scanning", warming: true });
      }
      const recognized = await recognition;
      if (presentationId !== recognitionPresentation.current) return;
      setOcrRegions((current) => [
        ...current.filter((item) => item.id !== recognized.id && item.id !== replacedRegionId),
        recognized
      ]);
      setReplacementRegion(null);
      setSupportState({ status: "done", region: recognized });
    } catch (reason) {
      if (presentationId === recognitionPresentation.current) {
        setSupportState({
          status: "error",
          message: reason instanceof Error ? reason.message : "Text recognition failed"
        });
      }
    } finally {
      recognitionInFlightRef.current = false;
      setRecognitionInFlight(false);
    }
  };

  const openSelection = (regions: OcrRegion[]) => {
    const region = regions[0];
    if (!region) return;
    ++recognitionPresentation.current;
    setReplacementRegion(null);
    setSupportState({ status: "done", region, groupedRegions: regions });
    setChromeVisible(true);
  };

  const activateGroupedSelection = (region: OcrRegion) => {
    setSupportState((current) => current.status === "done"
      ? { ...current, region }
      : current);
  };

  const updateSelection = (region: OcrRegion) => {
    setOcrRegions((current) => current.map((item) => item.id === region.id ? region : item));
    setReplacementRegion((current) => current?.id === region.id ? region : current);
    setSupportState((current) => current.status === "done" && current.region.id === region.id
      ? {
          ...current,
          region,
          groupedRegions: current.groupedRegions?.map((item) => item.id === region.id ? region : item)
        }
      : current);
  };

  const updateSelectionComprehension = (regionId: string, status: ComprehensionStatus | null) => {
    const mergeStatus = (region: OcrRegion): OcrRegion => region.id === regionId
      ? { ...region, comprehensionStatus: status }
      : region;
    setOcrRegions((current) => current.map(mergeStatus));
    setReplacementRegion((current) => current ? mergeStatus(current) : null);
    setSupportState((current) => {
      if (current.status !== "done") return current;
      const groupedRegions = current.groupedRegions?.map(mergeStatus);
      return current.region.id === regionId || groupedRegions?.some((region) => region.id === regionId)
        ? { ...current, region: mergeStatus(current.region), groupedRegions }
        : current;
    });
  };

  const removeSelection = async (region: OcrRegion) => {
    await deleteOcrRegion(region.id);
    const remaining = ocrRegions.filter((item) => item.id !== region.id);
    setOcrRegions(remaining);
    setReplacementRegion((current) => current?.id === region.id ? null : current);
    setSupportState((current) => {
      if (current.status === "done") {
        const groupedRegions = (current.groupedRegions ?? [current.region]).filter((item) => item.id !== region.id);
        if (groupedRegions.length > 0) {
          const active = current.region.id === region.id ? groupedRegions[0] : current.region;
          return { status: "done", region: active, groupedRegions };
        }
      }
      const nextRegion = remaining.find((item) => item.pageId === region.pageId);
      return nextRegion ? { status: "done", region: nextRegion, groupedRegions: [nextRegion] } : { status: "idle" };
    });
  };

  const redoSelection = (region: OcrRegion) => {
    setReplacementRegion(region);
    setAssistEnabled(true);
    setSupportState({ status: "idle" });
    setChromeVisible(true);
  };

  if (error) {
    return (
      <main className="reader-error">
        <BookOpen size={30} />
        <h1>Couldn’t open this manga</h1>
        <p>{error}</p>
        <button className="reader-button" type="button" onClick={() => navigate("/")}><ArrowLeft size={18} /> Library</button>
      </main>
    );
  }

  if (!book || !positionReady) {
    return <main className="reader-loading"><LoaderCircle className="spin" size={28} /><span>Opening manga…</span></main>;
  }

  return (
    <main
      className={`reader-shell ${chromeVisible ? "chrome-visible" : "chrome-hidden"} ${assistEnabled ? "assist-enabled" : ""}`}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerCancel}
    >
      <div
        ref={pagesRef}
        className={`reader-pages ${currentGroup.length > 1 ? "is-spread" : "is-single"}`}
        aria-hidden={vocabularyOpen || guidedViewOpen}
        inert={vocabularyOpen || guidedViewOpen}
      >
        {(isRtl ? [...currentGroup].reverse() : currentGroup).map((page) => (
          <ReaderPageView
            page={page}
            assistEnabled={assistEnabled}
            ocrRefreshing={ocrRefreshingPageIds.has(page.id)}
            selectionBusy={recognitionInFlight}
            regions={ocrRegions.filter((region) => region.pageId === page.id)}
            activeRegionId={supportState.status === "done" ? supportState.region.id : replacementRegion?.id}
            activeRegion={supportState.status === "done" && supportState.region.pageId === page.id ? supportState.region : undefined}
            supportPanelBounds={supportPanelBounds}
            onSelect={(selectedPage, region) => void recognizeSelection(selectedPage, region)}
            onOpenRegions={openSelection}
            onReady={markPageReady}
            key={page.id}
          />
        ))}
      </div>

      {guidedViewOpen && activePage && (
        <GuidedPanelView
          page={activePage}
          panel={guidedPanel}
          panelIndex={guidedPanelIndex}
          panelCount={guidedPanels.length}
          pageCount={book.pageCount}
          readingDirection={book.readingDirection}
          loading={guidedPanelLoading}
          message={guidedPanelMessage}
          regions={ocrRegions.filter((region) => region.pageId === activePage.id && regionIsInsidePanel(guidedPanel, region))}
          activeRegionId={supportState.status === "done" ? supportState.region.id : replacementRegion?.id}
          activeRegion={supportState.status === "done"
            && supportState.region.pageId === activePage.id
            && regionIsInsidePanel(guidedPanel, supportState.region)
            ? supportState.region
            : undefined}
          supportPanelBounds={supportPanelBounds}
          ocrRefreshing={ocrRefreshingPageIds.has(activePage.id)}
          selectionBusy={recognitionInFlight}
          canGoBack={!guidedPanelLoading && (guidedPanelIndex > 0 || currentPage > 0)}
          canGoForward={!guidedPanelLoading && (guidedPanelIndex < guidedPanels.length - 1 || currentPage < book.pageCount - 1)}
          onBack={previousGuidedPanel}
          onForward={nextGuidedPanel}
          onClose={closeGuidedView}
          onSelect={(selectedPage, region) => void recognizeSelection(selectedPage, region)}
          onOpenRegions={openSelection}
        />
      )}

      <div
        className={`reader-page-indicator ${pageIndicatorVisible ? "is-visible" : ""}`}
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        Page {Math.min(currentPage + 1, book.pageCount)} of {book.pageCount}
      </div>

      {activeGoalCelebration && (
        <div
          className={`reader-goal-celebration is-${activeGoalCelebration.kind}`}
          role="status"
          aria-live="polite"
          aria-atomic="true"
          key={`${activeGoalCelebration.kind}:${activeGoalCelebration.periodFrom}`}
        >
          <Sparkles size={19} strokeWidth={2.2} aria-hidden="true" />
          <span>
            <strong>{activeGoalCelebration.kind === "daily" ? "Daily goal reached" : "Weekly goal reached"}</strong>
            <small>{formatReadingTime(activeGoalCelebration.targetMinutes * 60_000)} {activeGoalCelebration.kind === "daily" ? "today" : "this week"}</small>
          </span>
        </div>
      )}

      <header className="reader-topbar" data-reader-control aria-hidden={!chromeVisible || guidedViewOpen} inert={!chromeVisible || vocabularyOpen || guidedViewOpen}>
        <button className="reader-icon-button" type="button" onClick={() => navigate("/")} aria-label="Back to library">
          <ArrowLeft size={21} />
        </button>
        <div className="reader-topbar-actions">
          <button
            className="reader-icon-button"
            type="button"
            onClick={openGuidedView}
            aria-label="Open Guided View"
            title="Guided View"
          >
            <PanelsTopLeft size={21} />
          </button>
          <button
            className={`reader-icon-button ${assistEnabled ? "is-active" : ""}`}
            type="button"
            onClick={() => {
              ++recognitionPresentation.current;
              setAssistEnabled((enabled) => {
                if (enabled) setReplacementRegion(null);
                return !enabled;
              });
              setChromeVisible(true);
              setSupportState({ status: "idle" });
            }}
            aria-label={assistEnabled ? "Turn off text selection" : "Select text for help"}
            aria-pressed={assistEnabled}
          >
            <ScanText size={21} />
          </button>
          <button
            ref={vocabularyButtonRef}
            className={`reader-icon-button ${vocabularyOpen ? "is-active" : ""}`}
            type="button"
            onClick={() => {
              setVocabularyOpen((open) => {
                if (open) return false;
                setSettingsOpen(false);
                return true;
              });
              setChromeVisible(true);
            }}
            aria-label="Open page review"
            aria-expanded={vocabularyOpen}
            aria-controls="reader-pace-drawer"
          >
            <Clock3 size={21} />
          </button>
          <button
            className="reader-icon-button"
            type="button"
            onClick={() => {
              setVocabularyOpen(false);
              setSettingsOpen((open) => !open);
            }}
            aria-label="Reader settings"
            aria-expanded={settingsOpen}
          >
            <MoreHorizontal size={22} />
          </button>
        </div>
      </header>

      <button
        className={`page-zone page-zone-left ${currentGroup.some((page) => page.isWide) ? "is-wide-page" : ""}`}
        type="button"
        onClick={isRtl ? next : previous}
        data-reader-control
        aria-label={isRtl ? "Next page" : "Previous page"}
        disabled={isRtl ? groupIndex >= layoutGroups.length - 1 : groupIndex === 0}
        aria-hidden={!chromeVisible || guidedViewOpen}
        tabIndex={chromeVisible && !guidedViewOpen ? 0 : -1}
        inert={!chromeVisible || vocabularyOpen || guidedViewOpen}
      >
        <ChevronLeft size={22} />
      </button>
      <button
        className={`page-zone page-zone-right ${currentGroup.some((page) => page.isWide) ? "is-wide-page" : ""}`}
        type="button"
        onClick={isRtl ? previous : next}
        data-reader-control
        aria-label={isRtl ? "Previous page" : "Next page"}
        disabled={isRtl ? groupIndex === 0 : groupIndex >= layoutGroups.length - 1}
        aria-hidden={!chromeVisible || guidedViewOpen}
        tabIndex={chromeVisible && !guidedViewOpen ? 0 : -1}
        inert={!chromeVisible || vocabularyOpen || guidedViewOpen}
      >
        <ChevronRight size={22} />
      </button>

      <footer className="reader-bottombar" data-reader-control aria-hidden={!chromeVisible || guidedViewOpen} inert={!chromeVisible || vocabularyOpen || guidedViewOpen}>
        <button className="reader-icon-button compact" type="button" onClick={previous} disabled={groupIndex === 0} aria-label="Previous page">
          {isRtl ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
        </button>
        <input
          className="page-slider"
          type="range"
          min={0}
          max={Math.max(layoutGroups.length - 1, 0)}
          value={groupIndex}
          onChange={(event) => moveToGroup(Number(event.target.value))}
          aria-label="Reading progress"
          style={{ direction: isRtl ? "rtl" : "ltr" }}
        />
        <button className="reader-icon-button compact" type="button" onClick={next} disabled={groupIndex >= layoutGroups.length - 1} aria-label="Next page">
          {isRtl ? <ChevronLeft size={20} /> : <ChevronRight size={20} />}
        </button>
      </footer>

      {settingsOpen && (
        <aside className="reader-settings" data-reader-control aria-label="Reader settings">
          <div className="settings-heading">
            <div>
              <p className="eyebrow">Display</p>
              <h2>Page layout</h2>
            </div>
            <button className="reader-icon-button compact" type="button" onClick={() => setSettingsOpen(false)} aria-label="Close settings"><X size={19} /></button>
          </div>
          <div className="segmented-control">
            <button className={layoutMode === "auto" ? "selected" : ""} type="button" onClick={() => changeLayout("auto")}>
              <PanelTopOpen size={17} /> Auto
            </button>
            <button className={layoutMode === "single" ? "selected" : ""} type="button" onClick={() => changeLayout("single")}>
              <PanelTopClose size={17} /> Single
            </button>
            <button className={layoutMode === "spread" ? "selected" : ""} type="button" onClick={() => changeLayout("spread")}>
              <BookOpen size={17} /> Spread
            </button>
          </div>
          <p className="settings-note">Landscape uses {isRtl ? "right-to-left" : "left-to-right"} spreads. Portrait and Split View stay on one page. Swipe horizontally to turn pages; tap the middle to show or hide the reading controls.</p>
        </aside>
      )}

      {vocabularyOpen && activePage && (
        <ReaderPaceDrawer book={book} page={activePage} visitMs={pageVisitMs} onClose={closeVocabulary} />
      )}

      <div aria-hidden={vocabularyOpen} inert={vocabularyOpen}>
        {replacementRegion && supportState.status === "idle" && (
          <div className="replacement-guidance" data-reader-control role="status">
            <span>Draw the selection again around the full text.</span>
            <button type="button" onClick={() => setReplacementRegion(null)}>Cancel</button>
          </div>
        )}

        <SupportPanel
          state={supportState}
          page={supportPage}
          onUpdateRegion={updateSelection}
          onUpdateComprehension={updateSelectionComprehension}
          onActivateRegion={activateGroupedSelection}
          onRedoRegion={redoSelection}
          onDeleteRegion={removeSelection}
          onBoundsChange={updateSupportPanelBounds}
          landscapeSide={supportLandscapeSide}
          onClose={() => {
            ++recognitionPresentation.current;
            setSupportState({ status: "idle" });
          }}
        />
      </div>
    </main>
  );
}

function ReaderPageView({
  page,
  assistEnabled,
  ocrRefreshing,
  selectionBusy,
  regions,
  activeRegionId,
  activeRegion,
  supportPanelBounds,
  onSelect,
  onOpenRegions,
  onReady
}: {
  page: MangaPage;
  assistEnabled: boolean;
  ocrRefreshing: boolean;
  selectionBusy: boolean;
  regions: OcrRegion[];
  activeRegionId?: string;
  activeRegion?: OcrRegion;
  supportPanelBounds: SupportPanelBounds | null;
  onSelect: (page: MangaPage, region: NormalizedRegion) => void;
  onOpenRegions: (regions: OcrRegion[]) => void;
  onReady: (pageId: string) => void;
}) {
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [loadFailed, setLoadFailed] = useState(false);
  const [pageShift, setPageShift] = useState(0);
  const retryTimer = useRef<number | null>(null);
  const pageRef = useRef<HTMLElement>(null);
  const imageWrapRef = useRef<HTMLDivElement>(null);
  const pageAspect = page.width / page.height;
  const imageUrl = loadAttempt === 0 ? page.imageUrl : `${page.imageUrl}?retry=${loadAttempt}`;

  useEffect(() => {
    setLoadAttempt(0);
    setLoadFailed(false);
    return () => {
      if (retryTimer.current) window.clearTimeout(retryTimer.current);
    };
  }, [page.id]);

  useLayoutEffect(() => {
    const pageElement = pageRef.current;
    const imageWrap = imageWrapRef.current;
    if (!pageElement || !imageWrap || !activeRegion || !supportPanelBounds) {
      setPageShift(0);
      return;
    }

    const updatePageShift = () => {
      const pageBounds = pageElement.getBoundingClientRect();
      const unshiftedPageTop = pageBounds.top + (pageElement.clientHeight - imageWrap.offsetHeight) / 2;
      const nextShift = readerPageOcclusionShift({
        pageLeft: imageWrap.getBoundingClientRect().left,
        pageTop: unshiftedPageTop,
        pageWidth: imageWrap.offsetWidth,
        pageHeight: imageWrap.offsetHeight,
        region: activeRegion,
        panel: supportPanelBounds
      });
      setPageShift((current) => Math.abs(current - nextShift) < 0.5 ? current : nextShift);
    };
    updatePageShift();
    const resizeObserver = new ResizeObserver(updatePageShift);
    resizeObserver.observe(pageElement);
    resizeObserver.observe(imageWrap);
    window.addEventListener("resize", updatePageShift);
    window.visualViewport?.addEventListener("resize", updatePageShift);
    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", updatePageShift);
      window.visualViewport?.removeEventListener("resize", updatePageShift);
    };
  }, [activeRegion, supportPanelBounds]);

  const retry = () => {
    if (retryTimer.current) window.clearTimeout(retryTimer.current);
    setLoadFailed(false);
    setLoadAttempt((attempt) => attempt + 1);
  };

  const handleLoadError = () => {
    if (loadAttempt >= 2) {
      setLoadFailed(true);
      return;
    }
    retryTimer.current = window.setTimeout(retry, 350 * (loadAttempt + 1));
  };

  return (
    <figure ref={pageRef} className={`reader-page ${page.isWide ? "is-wide" : ""}`}>
      <div
        ref={imageWrapRef}
        className="reader-image-wrap"
        style={{
          aspectRatio: `${page.width} / ${page.height}`,
          maxWidth: `${pageAspect * 100}dvh`,
          "--reader-page-shift": `${pageShift}px`
        } as CSSProperties}
      >
        <img
          src={imageUrl}
          width={page.width}
          height={page.height}
          alt={`Page ${page.index + 1}`}
          draggable="false"
          onError={handleLoadError}
          onLoad={() => {
            setLoadFailed(false);
            onReady(page.id);
          }}
        />
        <AssistOverlay
          page={page}
          enabled={assistEnabled}
          ocrRefreshing={ocrRefreshing}
          selectionBusy={selectionBusy}
          regions={regions}
          activeRegionId={activeRegionId}
          onSelect={onSelect}
          onOpenRegions={onOpenRegions}
        />
        {loadFailed && (
          <div className="reader-page-error" data-reader-control>
            <p>Page {page.index + 1} didn’t load.</p>
            <button type="button" onClick={retry}>Try again</button>
          </div>
        )}
      </div>
    </figure>
  );
}

function createLayoutGroups(pages: MangaPage[], spreads: boolean): MangaPage[][] {
  if (!spreads) return pages.map((page) => [page]);
  const groups: MangaPage[][] = [];
  let cursor = 0;
  while (cursor < pages.length) {
    const page = pages[cursor];
    if (page.chapterPageIndex === 0 || page.isWide) {
      groups.push([page]);
      cursor += 1;
      continue;
    }
    const next = pages[cursor + 1];
    if (next && next.chapterId === page.chapterId && !next.isWide && next.chapterPageIndex !== 0) {
      groups.push([page, next]);
      cursor += 2;
    } else {
      groups.push([page]);
      cursor += 1;
    }
  }
  return groups;
}
